import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch, useApp } from "../../context/AppContext";
import { useDepartments } from "../../hooks/useDepartments";
import { useUsers } from "../../hooks/useUsers";
import {
  AlertTriangle,
  Bell,
  Clock,
  CheckCircle2,
  Check,
  Download,
  Plus,
  Filter,
  Calendar,
  Info,
  Sun,
  Moon,
  Scale,
  MoreVertical,
  Settings,
  Eye,
  FileText,
  Send,
  Archive,
  Briefcase,
  TrendingUp,
  User,
  Shield,
  ShieldAlert,
  Upload,
  Trash2,
  Paperclip,
  Activity,
  ArrowLeft,
  FileDown,
  FileSpreadsheet,
  ChevronDown,
  Search,
  Mail,
  Pencil,
  X,
  Loader2,
  Flag
} from "lucide-react";

import {
  exportSubmissionPdf,
  exportSubmissionExcel,
  safeExportFilename
} from "../../utils/exportSubmissionPdf";
import { confirmAction, showError, showInfo, showWarning } from "../../utils/sweetAlert";

/* ─── helpers ────────────────────────────────────────────────────────────── */
function cls(...args) { return args.filter(Boolean).join(" "); }

function formatFriendlyDateTime(dateVal) {
  if (!dateVal) return "—";
  const dateObj = new Date(dateVal);
  if (isNaN(dateObj.getTime())) return "—";
  return dateObj.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** Agent breach Updated On — show last status edit, not email-log touch times. */
function formatAgentBreachUpdatedOn(item) {
  if (!item) return "—";
  const hasStatusEdit =
    (item.resolution_comment && String(item.resolution_comment).trim()) ||
    (item.status && !["Investigating"].includes(item.status));
  if (!hasStatusEdit) {
    return formatFriendlyDateTime(item.created_at || item.createdAt);
  }
  return formatFriendlyDateTime(item.updated_at || item.updatedAt);
}

/* ─── mock data ─────────────────────────────────────────────────────────── */
const INITIAL_BREACHES = [
  {
    id: "default-mock-breach-id",
    formattedId: "BR-2024-031",
    incident: "Unauthorized access to customer payment data detected.",
    description: "Access revoked, affected systems isolated. Customer payment gateway API credentials leaked.",
    category: "Confidentiality",
    severity: "Critical",
    dpsAffected: "12,847",
    status: "Investigating",
    detectionDateTime: "2024-06-16T09:41:00Z",
    dpoNotified: "Yes",
    dpoNotificationDate: "2024-06-16T10:45:00Z",
    dpbNotificationRequired: "Yes",
    dataPrincipalNotificationRequired: "Yes",
    emailLogs: [],
    action: { label: "Manage", variant: "default" }
  }
];

/* ─── animation configurations ──────────────────────────────────────────── */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 15 }
  }
};

/* ─── component helper badges ────────────────────────────────────────────── */
function SeverityBadge({ severity, dark }) {
  const map = {
    Critical: {
      bg: dark ? "bg-red-950/40" : "bg-red-50",
      text: dark ? "text-red-400" : "text-red-700",
      border: dark ? "border-red-900/30" : "border-red-100",
      dot: "bg-red-500"
    },
    High: {
      bg: dark ? "bg-orange-950/40" : "bg-orange-50",
      text: dark ? "text-orange-400" : "text-orange-700",
      border: dark ? "border-orange-900/30" : "border-orange-100",
      dot: "bg-orange-500"
    },
    Medium: {
      bg: dark ? "bg-amber-950/40" : "bg-amber-550",
      text: dark ? "text-amber-400" : "text-amber-700",
      border: dark ? "border-amber-900/30" : "border-amber-100",
      dot: "bg-amber-550"
    },
    Low: {
      bg: dark ? "bg-emerald-950/40" : "bg-emerald-50",
      text: dark ? "text-emerald-400" : "text-emerald-700",
      border: dark ? "border-emerald-900/30" : "border-emerald-100",
      dot: "bg-emerald-500"
    }
  };
  const s = map[severity] || map.Low;
  return (
    <span className={cls("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border select-none", s.bg, s.text, s.border)}>
      <span className={cls("w-1.5 h-1.5 rounded-full flex-shrink-0", s.dot)} />
      {severity}
    </span>
  );
}

function StatusBadge({ status, dark }) {
  const map = {
    Investigating: {
      dot: "bg-amber-500",
      text: dark ? "text-amber-400" : "text-amber-750",
      bg: dark ? "bg-amber-950/20 border-amber-900/30" : "bg-amber-50 border-amber-100"
    },
    "Under Review": {
      dot: "bg-orange-500",
      text: dark ? "text-orange-400" : "text-orange-750",
      bg: dark ? "bg-orange-950/20 border-orange-900/30" : "bg-orange-50 border-orange-100"
    },
    "DPB Notified": {
      dot: "bg-emerald-500",
      text: dark ? "text-emerald-400" : "text-emerald-750",
      bg: dark ? "bg-emerald-950/20 border-emerald-900/30" : "bg-emerald-50 border-emerald-100"
    },
    Assessing: {
      dot: "bg-blue-500",
      text: dark ? "text-blue-400" : "text-blue-750",
      bg: dark ? "bg-blue-950/20 border-blue-900/30" : "bg-blue-50 border-blue-100"
    },
    Remediated: {
      dot: "bg-emerald-500",
      text: dark ? "text-emerald-400" : "text-emerald-750",
      bg: dark ? "bg-emerald-950/20 border-emerald-900/30" : "bg-emerald-50 border-emerald-100"
    },
    Closed: {
      dot: "bg-emerald-500",
      text: dark ? "text-emerald-400" : "text-emerald-750",
      bg: dark ? "bg-emerald-950/20 border-emerald-900/30" : "bg-emerald-50 border-emerald-100",
      label: "Resolved"
    }
  };
  const s = map[status] || { dot: "bg-slate-400", text: "text-slate-500", bg: "bg-slate-50 border-slate-200" };
  return (
    <span className={cls("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold border", s.bg, s.text)}>
      <span className={cls("w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse", s.dot)} />
      {s.label || status}
    </span>
  );
}

function ClockBadge({ dpbClock, dark }) {
  if (dpbClock.label === "—") {
    return <span className="text-xs text-slate-450 font-bold px-2">—</span>;
  }
  if (dpbClock.closed) {
    return (
      <span className={cls("inline-flex items-center gap-1 text-xs font-extrabold px-2 py-0.5 rounded border uppercase", dark ? "bg-emerald-950/20 border-emerald-900 text-emerald-400" : "bg-emerald-50 border-emerald-100 text-emerald-700")}>
        <Check size={12} className="stroke-[3px]" />
        {dpbClock.label}
      </span>
    );
  }
  if (dpbClock.notified) {
    return (
      <span className={cls("inline-flex items-center gap-1 text-xs font-bold", dark ? "text-emerald-400" : "text-emerald-600")}>
        <Check size={12} className="stroke-[3px]" />
        {dpbClock.label}
      </span>
    );
  }
  if (dpbClock.overdue) {
    return (
      <span className={cls("inline-flex items-center gap-1 text-xs font-extrabold tracking-wide px-2 py-0.5 rounded border uppercase animate-pulse", dark ? "bg-red-950/40 border-red-900 text-red-400" : "bg-red-50 border-red-100 text-red-700")}>
        {dpbClock.label}
      </span>
    );
  }
  return (
    <span className={cls("inline-flex items-center gap-1 text-xs font-extrabold text-orange-500")}>
      <Clock size={12} className="animate-pulse" />
      {dpbClock.label}
    </span>
  );
}

function getDpbClock(detectionDateTimeStr, dpoNotified, now, status, updatedAtStr) {
  if (!detectionDateTimeStr) {
    return { label: "—", overdue: false, notified: false, closed: false };
  }
  const isClosed = status === "Closed" || status === "Remediated";
  try {
    const detectionTime = new Date(detectionDateTimeStr);
    const deadline = new Date(detectionTime.getTime() + 72 * 3600 * 1000);
    const referenceTime = isClosed && updatedAtStr ? new Date(updatedAtStr) : now;
    const diffMs = deadline.getTime() - referenceTime.getTime();

    const diffSecs = Math.max(0, Math.floor(diffMs / 1000));
    const isOverdue = diffSecs <= 0;

    let label = "";
    if (isClosed) {
      if (isOverdue) {
        label = "Resolved (Overdue)";
      } else {
        const hours = Math.floor(diffSecs / 3600);
        const mins = Math.floor((diffSecs % 3600) / 60);
        const secs = diffSecs % 60;
        label = `Resolved (${hours}h ${String(mins).padStart(2, '0')}m left)`;
      }
    } else {
      if (isOverdue) {
        label = "Overdue";
      } else {
        const hours = Math.floor(diffSecs / 3600);
        const mins = Math.floor((diffSecs % 3600) / 60);
        const secs = diffSecs % 60;
        label = `${hours}h ${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s left`;
      }
    }

    return {
      label,
      overdue: isOverdue,
      notified: dpoNotified === "Yes",
      closed: isClosed
    };
  } catch (e) {
    return { label: "—", overdue: false, notified: false, closed: false };
  }
}

function getDynamicChecklist(breach, now) {
  if (!breach) return [];

  const status = breach.status || "Investigating";
  const dpsCount = breach.dpsAffected || "0";
  const formattedId = breach.formattedId || breach.id;

  const list = [];

  // 1. Breach contained
  const isContained = status !== "Investigating" && status !== "Assessing";
  list.push({
    label: "Breach contained",
    sub: isContained ? "Access revoked, affected systems isolated" : "Containment and system isolation in progress",
    done: isContained
  });

  // 2. Impact assessment completed
  const isImpactAssessed = status !== "Investigating" && status !== "Assessing" && dpsCount !== "0" && dpsCount !== 0;
  list.push({
    label: "Impact assessment completed",
    sub: isImpactAssessed ? `${dpsCount} data principals affected identified` : "Identification and scope assessment in progress",
    done: isImpactAssessed
  });

  // 3. DPO notified within 6 hours
  if (breach.dpoNotified === "Yes") {
    let dpoSub = "DPO notification pending";
    if (breach.dpoNotificationDate) {
      const d = new Date(breach.dpoNotificationDate);
      if (!isNaN(d.getTime())) {
        dpoSub = `Notified at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} on ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
      } else {
        dpoSub = `Notified on ${breach.dpoNotificationDate}`;
      }
    } else if (breach.detectionDateTime) {
      const d = new Date(breach.detectionDateTime);
      d.setMinutes(d.getMinutes() + 84); // simulate 1h 24m escalation like BR-2024-031
      dpoSub = `Notified at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} on ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
    } else {
      dpoSub = "DPO Notified";
    }
    list.push({
      label: "DPO notified within 6h",
      sub: dpoSub,
      done: true
    });
  }

  // 4. DPB notification drafted
  if (breach.dpbNotificationRequired === "Yes") {
    const isDrafted = ["Under Review", "DPB Notified", "Remediated", "Closed"].includes(status);
    list.push({
      label: "DPB notification drafted",
      sub: isDrafted ? `Form ${formattedId} ready for submission` : `Drafting DPB notification form ${formattedId}`,
      done: isDrafted
    });

    // 5. DPB notification submitted
    const isSubmitted = ["DPB Notified", "Remediated", "Closed"].includes(status);
    let dbpDueStr = "";
    if (breach.detectionDateTime) {
      const date = new Date(breach.detectionDateTime);
      date.setHours(date.getHours() + 72);
      dbpDueStr = `Deadline: ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} IST`;
    } else {
      dbpDueStr = "72h from detection";
    }
    list.push({
      label: "DPB notification submitted",
      sub: isSubmitted ? "Submitted to Data Protection Board" : dbpDueStr,
      done: isSubmitted
    });
  }

  // 6. Data principals notified
  if (breach.dataPrincipalNotificationRequired === "Yes") {
    const isDpNotified = ["Remediated", "Closed"].includes(status);
    list.push({
      label: "Data principals notified",
      sub: isDpNotified ? "Notified affected data principals" : "Required if harm is likely",
      done: isDpNotified
    });
  }

  // 7. Remediation plan executed
  const isRemediated = ["Remediated", "Closed"].includes(status);
  list.push({
    label: "Remediation plan executed",
    sub: isRemediated ? "Post-incident corrective controls verified" : "Remediation measures under development",
    done: isRemediated
  });

  // 8. Closure report filed
  const isClosed = status === "Closed";
  list.push({
    label: "Closure report filed",
    sub: isClosed ? "Closure report accepted by DPB" : "Pending final resolution and closure",
    done: isClosed
  });

  return list;
}

function getDynamicTimeline(breach, now) {
  if (!breach) return [];

  const status = breach.status || "Investigating";

  let detectDate = null;
  let detectTime = null;
  if (breach.detectionDateTime) {
    const d = new Date(breach.detectionDateTime);
    detectDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    detectTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  const list = [];

  // 1. Breach Detected
  list.push({ label: "Breach Detected", date: detectDate, time: detectTime, done: true, warn: false });

  // 2. Internal Escalation
  let escDate = null;
  let escTime = null;
  const escTimestamp = breach.createdAt || breach.created_at;
  if (escTimestamp) {
    let d = new Date(escTimestamp);
    if (breach.dpoNotificationDate) {
      const dpoDateObj = new Date(breach.dpoNotificationDate);
      if (d > dpoDateObj && !isNaN(dpoDateObj.getTime())) {
        d = dpoDateObj;
      }
    }
    escDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    escTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } else if (breach.detectionDateTime) {
    const d = new Date(breach.detectionDateTime);
    d.setMinutes(d.getMinutes() + 45);
    escDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    escTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  list.push({ label: "Internal Escalation", date: escDate, time: escTime, done: true, warn: false });

  // 3. DPO Notified
  if (breach.dpoNotified === "Yes") {
    let dpoDate = null;
    let dpoTime = null;
    if (breach.dpoNotificationDate) {
      const d = new Date(breach.dpoNotificationDate);
      dpoDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      dpoTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } else if (breach.detectionDateTime) {
      const d = new Date(breach.detectionDateTime);
      d.setHours(d.getHours() + 1);
      dpoDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      dpoTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    list.push({ label: "DPO Notified", date: dpoDate, time: dpoTime, done: true, warn: false });
  }

  // 4. DPB Notification
  if (breach.dpbNotificationRequired === "Yes") {
    const isDpbNotified = ["DPB Notified", "Remediated", "Closed"].includes(status);
    let dpbDate = null;
    let dpbTime = null;
    if (isDpbNotified) {
      let d = null;
      const updatedTimestamp = breach.updatedAt || breach.updated_at;
      if (updatedTimestamp) {
        d = new Date(updatedTimestamp);
      } else if (breach.detectionDateTime) {
        d = new Date(breach.detectionDateTime);
        d.setHours(d.getHours() + 23);
      }
      if (d && !isNaN(d.getTime())) {
        dpbDate = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        dpbTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      }
    }
    const clock = getDpbClock(breach.detectionDateTime, breach.dpoNotified, now, status, breach.updatedAt);
    list.push({
      label: "DPB Notification",
      date: dpbDate,
      time: dpbTime,
      done: isDpbNotified,
      warn: !isDpbNotified && clock.overdue,
      due: !isDpbNotified
    });
  }

  // 5. Data Principal Notice
  if (breach.dataPrincipalNotificationRequired === "Yes") {
    const isDpNotified = ["Remediated", "Closed"].includes(status);
    list.push({
      label: "Data Principal Notice",
      date: null,
      time: null,
      done: isDpNotified,
      warn: false,
      sub: "if harm likely"
    });
  }

  // 6. Remediation
  const isRemediated = ["Remediated", "Closed"].includes(status);
  list.push({
    label: "Remediation",
    date: null,
    time: null,
    done: isRemediated,
    warn: false,
    sub: "Post-notification"
  });

  // 7. Closure Report
  const isClosed = status === "Closed";
  list.push({
    label: "Closure Report",
    date: null,
    time: null,
    done: isClosed,
    warn: false,
    sub: "DPB requirement"
  });

  return list;
}


/* ─── stat card ──────────────────────────────────────────────────────────── */
function StatCard({ label, value, color, sub, icon: Icon, dark }) {
  const textColors = {
    red: dark ? "text-red-400" : "text-red-600",
    amber: dark ? "text-amber-400" : "text-amber-600",
    blue: dark ? "text-blue-400" : "text-blue-600",
    emerald: dark ? "text-emerald-400" : "text-emerald-600",
    indigo: dark ? "text-indigo-400" : "text-indigo-600"
  };
  const iconBgs = {
    red: dark ? "bg-red-950/40 text-red-400 border-red-900/30" : "bg-red-50 text-red-500 border-red-100",
    amber: dark ? "bg-amber-950/40 text-amber-400 border-amber-900/30" : "bg-amber-50 text-amber-500 border-amber-100",
    blue: dark ? "bg-blue-950/40 text-blue-400 border-blue-900/30" : "bg-blue-50 text-blue-500 border-blue-100",
    emerald: dark ? "bg-emerald-950/40 text-emerald-400 border-emerald-900/30" : "bg-emerald-50 text-emerald-500 border-emerald-100",
    indigo: dark ? "bg-indigo-950/40 text-indigo-400 border-indigo-900/30" : "bg-indigo-50 text-indigo-500 border-indigo-100"
  };

  return (
    <div
      className={cls(
        "rounded-2xl p-5 border flex flex-col justify-between transition-all duration-300 relative overflow-hidden shadow-sm hover:-translate-y-1",
        dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60 hover:border-slate-300 hover:shadow-md"
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <span className={cls("text-sm font-black tracking-wider uppercase", dark ? "text-slate-300" : "text-slate-800")}>
            {label}
          </span>
          <h2 className={cls("text-2xl font-extrabold mt-2 leading-none font-display", textColors[color])} style={{ fontFamily: "'Sora', sans-serif" }}>
            {value}
          </h2>
        </div>
        <div className={cls("p-3 rounded-full border flex items-center justify-center shadow-sm transition-all duration-300", iconBgs[color])}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-4 font-semibold">{sub}</p>
    </div>
  );
}

/* ─── main component ─────────────────────────────────────────────────────── */
export default function BreachManagement() {
  const dark = false;
  const { showNotification } = useApp();
  const { departments } = useDepartments();
  const { users } = useUsers();
  const [severityFilter, setSeverityFilter] = useState("All Severity");
  const [searchQuery, setSearchQuery] = useState("");
  const [clockSeconds, setClockSeconds] = useState(42 * 3600 + 15 * 60);
  const [now, setNow] = useState(() => new Date());
  const [breachesList, setBreachesList] = useState([]);

  const [selectedBreaches, setSelectedBreaches] = useState([]);
  const [selectedBreachId, setSelectedBreachId] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedBreachLogs, setSelectedBreachLogs] = useState([]);
  const [selectedBreachIdForLogs, setSelectedBreachIdForLogs] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const [agentCurrentPage, setAgentCurrentPage] = useState(1);
  const [agentTotalPages, setAgentTotalPages] = useState(1);
  const [agentTotalCount, setAgentTotalCount] = useState(0);
  const [agentActiveCount, setAgentActiveCount] = useState(0);
  const [agentPendingCount, setAgentPendingCount] = useState(0);
  const [agentResolvedCount, setAgentResolvedCount] = useState(0);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingTablePdf, setExportingTablePdf] = useState(false);
  const [exportingTableExcel, setExportingTableExcel] = useState(false);
  const [showTopExportMenu, setShowTopExportMenu] = useState(false);
  const [showTableExportMenu, setShowTableExportMenu] = useState(false);

  const [viewingAgentBreaches, setViewingAgentBreaches] = useState(false);
  const [agentBreachesList, setAgentBreachesList] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [dismissingAgentId, setDismissingAgentId] = useState(null);
  const [selectedTextModal, setSelectedTextModal] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const fetchAgentBreaches = async (page = 1, search = debouncedSearchQuery) => {
    setAgentLoading(true);
    try {
      const searchParam = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";
      const response = await apiFetch(`/agent-breaches/?page=${page}&page_size=${PAGE_SIZE}${searchParam}`);
      if (response.ok) {
        const data = await response.json();
        const items = data.items || [];
        const mapped = items.map((item, idx) => {
          const year = item.created_at ? new Date(item.created_at).getFullYear() : 2026;
          const overallIndex = data.total - ((page - 1) * PAGE_SIZE + idx);
          const serialStr = String(overallIndex).padStart(3, '0');
          return {
            ...item,
            formattedId: `AG-${year}-${serialStr}`,
            emailLogs: item.email_logs || [],
            detectionDateTime: item.created_at,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            reportedBy: item.source || "Security Agent EDR",
            severity: "High",
            dpsAffected: "0",
            status: item.status || "Investigating",
            dpoNotified: "Yes",
            dpbNotificationRequired: "Yes",
            dataPrincipalNotificationRequired: "No",
            incident: item.subject || "EDR Agent Alert",
            description: item.body_text || "Automated security breach alert.",
            category: item.label_name || "Security Alert",
            affectedSystem: item.source || "—"
          };
        });
        setAgentBreachesList(mapped);
        setAgentCurrentPage(data.page || page);
        setAgentTotalPages(data.pages || 1);
        setAgentTotalCount(data.total || 0);
        setAgentActiveCount(data.active_count || 0);
        setAgentPendingCount(data.pending_count || 0);
        setAgentResolvedCount(data.resolved_count || 0);
      }
    } catch (err) {
      console.error("Error loading agent breaches:", err);
    } finally {
      setAgentLoading(false);
    }
  };

  const handleDismissAgentBreach = async (id) => {
    if (!id) return;
    if (!(await confirmAction({
      title: "Dismiss agent breach?",
      text: "Are you sure you want to dismiss this agent breach?",
      confirmText: "Dismiss",
      icon: "warning",
    }))) return;
    setDismissingAgentId(id);
    try {
      const response = await apiFetch(`/agent-breaches/${id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        showNotification("Agent breach dismissed successfully!", "success");
        fetchAgentBreaches(agentCurrentPage);
      } else {
        showNotification("Failed to dismiss agent breach.", "error");
      }
    } catch (err) {
      console.error("Error dismissing agent breach:", err);
      showNotification("Error dismissing agent breach.", "error");
    } finally {
      setDismissingAgentId(null);
    }
  };

  const handleSendAgentBreachEmail = async (id) => {
    if (!id) return;
    setSendingEmailId(id);
    try {
      const response = await apiFetch(`/agent-breaches/${id}/send-email`, {
        method: "POST"
      });
      if (response.ok) {
        const updated = await response.json();
        const updatedList = agentBreachesList.map((b) => {
          if (b.id === id) {
            const mapped = {
              ...b,
              emailLogs: updated.email_logs || []
            };
            if (sidebarBreach && sidebarBreach.id === id) {
              setSidebarBreach(mapped);
            }
            return mapped;
          }
          return b;
        });
        setAgentBreachesList(updatedList);
        showNotification("Agent breach alert email sent successfully!", "success");
      } else {
        let errorMsg = "Failed to send agent breach email.";
        try {
          const errorData = await response.json();
          if (errorData && errorData.detail) {
            errorMsg = errorData.detail;
          }
        } catch (e) {}
        showNotification(errorMsg, "error");
      }
    } catch (err) {
      console.error("Error sending agent breach email:", err);
      showNotification("Error sending agent breach email.", "error");
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const toAdd = paginatedBreaches.filter(b => !selectedBreaches.includes(b.id)).map(b => b.id);
      setSelectedBreaches([...selectedBreaches, ...toAdd]);
    } else {
      const pageIds = paginatedBreaches.map(b => b.id);
      setSelectedBreaches(selectedBreaches.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectRow = (id) => {
    if (selectedBreaches.includes(id)) {
      setSelectedBreaches(selectedBreaches.filter((item) => item !== id));
    } else {
      setSelectedBreaches([...selectedBreaches, id]);
    }
  };

  // Form states (Log New Breach wizard)
  const [showLogForm, setShowLogForm] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState("");
  const [category, setCategory] = useState("Confidentiality");
  const [severity, setSeverity] = useState("Critical");
  const [detectionDateTime, setDetectionDateTime] = useState(() => {
    const local = new Date();
    const offset = local.getTimezoneOffset();
    const localDate = new Date(local.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().slice(0, 16);
  });
  const [reportedBy, setReportedBy] = useState("");
  const [department, setDepartment] = useState("");
  const [description, setDescription] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [affectedSystem, setAffectedSystem] = useState("");
  const [affectedDataTypes, setAffectedDataTypes] = useState(["Customer Data"]);
  const [dpsAffected, setDpsAffected] = useState("");
  const [recordsImpacted, setRecordsImpacted] = useState("");
  const [financialImpact, setFinancialImpact] = useState("");
  const [riskLevel, setRiskLevel] = useState("Critical");
  const [potentialImpactDesc, setPotentialImpactDesc] = useState("");
  const [dpoNotified, setDpoNotified] = useState("Yes");
  const [dpbNotificationRequired, setDpbNotificationRequired] = useState("Yes");
  const [dpoNotificationDate, setDpoNotificationDate] = useState(() => {
    const local = new Date();
    const offset = local.getTimezoneOffset();
    const localDate = new Date(local.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().slice(0, 16);
  });
  const [dataPrincipalNotificationRequired, setDataPrincipalNotificationRequired] = useState("Yes");
  const [regulatoryAuthority, setRegulatoryAuthority] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [wizardStep, setWizardStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [editBreachId, setEditBreachId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveBreachId, setResolveBreachId] = useState(null);
  const [resolveComment, setResolveComment] = useState("");
  const [resolveFiles, setResolveFiles] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDetailSidebar, setShowDetailSidebar] = useState(false);
  const [sidebarBreach, setSidebarBreach] = useState(null);
  const [sidebarActiveTab, setSidebarActiveTab] = useState("logs");
  const [editLogs, setEditLogs] = useState([]);
  const [editLogsLoading, setEditLogsLoading] = useState(false);

  const emailLogsOnly = sidebarBreach ? (sidebarBreach.emailLogs || []).filter(log => log.type !== "update") : [];


  const handleNewBreachClick = () => {
    setEditBreachId(null);
    setIncidentTitle("");
    setReportedBy("");
    setDescription("");
    setRootCause("");
    setAffectedSystem("");
    setDpsAffected("");
    setRecordsImpacted("");
    setFinancialImpact("");
    setPotentialImpactDesc("");
    setRegulatoryAuthority("");
    setUploadedFiles([]);
    setCategory("Confidentiality");
    setSeverity("Critical");
    setRiskLevel("Critical");
    setDpoNotified("Yes");
    setDpbNotificationRequired("Yes");
    setDataPrincipalNotificationRequired("Yes");
    setAffectedDataTypes(["Customer Data"]);

    const local = new Date();
    const offset = local.getTimezoneOffset();
    const localDate = new Date(local.getTime() - (offset * 60 * 1000));
    setDetectionDateTime(localDate.toISOString().slice(0, 16));
    setDpoNotificationDate(localDate.toISOString().slice(0, 16));

    setWizardStep(1);
    setFormErrors({});
    setShowLogForm(true);
  };

  const handleEditBreachClick = (breach) => {
    if (["Closed", "Remediated"].includes(breach.status)) {
      showNotification("Resolved incidents cannot be edited.", "error");
      return;
    }
    setEditBreachId(breach.id);
    setIncidentTitle(breach.incident || "");
    setCategory(breach.category || "Confidentiality");
    setSeverity(breach.severity || "Critical");

    if (breach.detectionDateTime) {
      const dt = new Date(breach.detectionDateTime);
      const offset = dt.getTimezoneOffset();
      const localDate = new Date(dt.getTime() - (offset * 60 * 1000));
      setDetectionDateTime(localDate.toISOString().slice(0, 16));
    } else {
      setDetectionDateTime("");
    }

    setReportedBy(breach.reportedBy || breach.reported_by || "");
    setDepartment(breach.department || "");
    setDescription(breach.description || "");
    setRootCause(breach.rootCause || breach.root_cause || "");
    setAffectedSystem(breach.affectedSystem || breach.affected_system || "");
    setAffectedDataTypes(breach.affectedDataTypes || breach.affected_data_types || []);
    setDpsAffected(breach.dpsAffected ? String(breach.dpsAffected).replace(/,/g, '') : "");
    setRecordsImpacted(breach.recordsImpacted ? String(breach.recordsImpacted).replace(/,/g, '') : "");
    setFinancialImpact(breach.financialImpact || breach.financial_impact || "");
    setRiskLevel(breach.riskLevel || breach.risk_level || breach.severity || "Critical");
    setPotentialImpactDesc(breach.potentialImpactDesc || breach.potential_impact_desc || "");
    setDpoNotified(breach.dpoNotified || breach.dpo_notified || "Yes");
    setDpbNotificationRequired(breach.dpbNotificationRequired || breach.dpb_notification_required || "Yes");

    if (breach.dpoNotificationDate) {
      const dt = new Date(breach.dpoNotificationDate);
      const offset = dt.getTimezoneOffset();
      const localDate = new Date(dt.getTime() - (offset * 60 * 1000));
      setDpoNotificationDate(localDate.toISOString().slice(0, 16));
    } else if (breach.dpo_notification_date) {
      const dt = new Date(breach.dpo_notification_date);
      const offset = dt.getTimezoneOffset();
      const localDate = new Date(dt.getTime() - (offset * 60 * 1000));
      setDpoNotificationDate(localDate.toISOString().slice(0, 16));
    } else {
      setDpoNotificationDate("");
    }

    setDataPrincipalNotificationRequired(breach.dataPrincipalNotificationRequired || breach.data_principal_notification_required || "Yes");
    setRegulatoryAuthority(breach.regulatoryAuthority || breach.regulatory_authority || "");
    setUploadedFiles(breach.uploadedFiles || breach.uploaded_files || []);

    setWizardStep(1);
    setFormErrors({});
    setShowLogForm(true);
  };

  const handleSendEmail = async (breach) => {
    if (!breach || !breach.id) return;
    setSendingEmailId(breach.id);
    try {
      const response = await apiFetch(`/breaches/${breach.id}/send-email`, {
        method: "POST"
      });
      if (response.ok) {
        const updated = await response.json();

        let updatedMailObj = null;
        // Update the breach in breachesList to have the new emailLogs
        const updatedList = breachesList.map((b) => {
          if (b.id === breach.id) {
            const updatedObj = {
              ...b,
              emailLogs: updated.email_logs || []
            };
            updatedMailObj = updatedObj;
            return updatedObj;
          }
          return b;
        });
        setBreachesList(updatedList);

        if (sidebarBreach && sidebarBreach.id === breach.id && updatedMailObj) {
          setSidebarBreach(updatedMailObj);
        }

        // Find recipient from emailLogs
        const lastLog = updated.email_logs && updated.email_logs[updated.email_logs.length - 1];
        const recipient = lastLog ? lastLog.recipient : (breach.reportedBy || breach.reported_by || "reporter");

        showNotification(`Alert email sent successfully to ${recipient}!`, "success");
      } else {
        const errorData = await response.json();
        console.error("Failed to send manual email alert:", errorData);
        showError(errorData.detail || "Failed to send manual email alert. Please verify organization settings.");
      }
    } catch (err) {
      console.error("Error connecting to send email endpoint:", err);
      showError("Error connecting to send email endpoint. Please try again.");
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleResolveFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg";
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      const newFiles = files.map(file => file.name);
      setResolveFiles(prev => {
        const updated = [...prev];
        newFiles.forEach(name => {
          if (!updated.includes(name)) {
            updated.push(name);
          }
        });
        return updated;
      });
    };
    input.click();
  };

  const handleResolveFileDelete = (file) => {
    setResolveFiles(resolveFiles.filter((f) => f !== file));
  };

  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    if (!resolveComment.trim()) {
      showWarning("Please provide a resolution comment/description.");
      return;
    }
    if (resolving) return;

    setResolving(true);
    const isAgent = agentBreachesList.some(item => item.id === resolveBreachId);

    const payload = isAgent ? {
      status: "Closed",
      resolution_comment: resolveComment
    } : {
      status: "Closed",
      resolution_comment: resolveComment,
      resolution_files: resolveFiles
    };

    try {
      const endpoint = isAgent ? `/agent-breaches/${resolveBreachId}` : `/breaches/${resolveBreachId}`;
      const response = await apiFetch(endpoint, {
        method: "PATCH",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const saved = await response.json();

        if (isAgent) {
          const updatedList = agentBreachesList.map((b) => {
            if (b.id === resolveBreachId) {
              const mappedObj = {
                ...b,
                status: saved.status,
                resolution_comment: saved.resolution_comment,
                updated_at: saved.updated_at || new Date().toISOString(),
                emailLogs: saved.email_logs || []
              };
              if (sidebarBreach && sidebarBreach.id === resolveBreachId) {
                setSidebarBreach(mappedObj);
              }
              return mappedObj;
            }
            return b;
          });
          setAgentBreachesList(updatedList);
        } else {
          let updatedResolveObj = null;
          // Update local list
          const updatedList = breachesList.map((b) => {
            if (b.id === resolveBreachId) {
              const updatedObj = {
                ...b,
                status: saved.status,
                updatedAt: saved.updated_at || new Date().toISOString(),
                emailLogs: saved.email_logs || [],
                resolutionComment: saved.resolution_comment || "",
                resolutionFiles: saved.resolution_files || []
              };
              updatedResolveObj = updatedObj;
              return updatedObj;
            }
            return b;
          });
          setBreachesList(updatedList);

          if (sidebarBreach && sidebarBreach.id === resolveBreachId && updatedResolveObj) {
            setSidebarBreach(updatedResolveObj);
          }
        }

        showNotification("Breach marked as Resolved/Closed successfully!", "success");
        setShowResolveModal(false);
        setResolveBreachId(null);
        setResolveComment("");
        setResolveFiles([]);
      } else {
        const errorData = await response.json();
        console.error("Failed to resolve breach:", errorData);
        showError("Failed to resolve breach. Please try again.");
      }
    } catch (err) {
      console.error("Error calling resolve endpoint:", err);
      showError("Error connecting to resolve endpoint. Please try again.");
    } finally {
      setResolving(false);
    }
  };



  const handleIdClick = async (breach) => {
    setSidebarBreach(breach);
    setSidebarActiveTab("logs");
    setEditLogs([]);
    setShowDetailSidebar(true);
    // Fetch edit logs from dedicated table
    setEditLogsLoading(true);
    try {
      const isAgent = agentBreachesList.some(item => item.id === breach.id) || (breach.formattedId && breach.formattedId.startsWith("AG-"));
      const endpoint = isAgent ? `/agent-breaches/${breach.id}/edit-logs` : `/breaches/${breach.id}/edit-logs`;
      const res = await apiFetch(endpoint);
      if (res.ok) {
        const logs = await res.json();
        setEditLogs(logs);
      }
    } catch (err) {
      console.error("Failed to fetch edit logs:", err);
    } finally {
      setEditLogsLoading(false);
    }
  };

  const handleSidebarSendEmail = async () => {
    if (!sidebarBreach || !sidebarBreach.id) return;
    setSendingEmailId(sidebarBreach.id);
    try {
      const isAgent = agentBreachesList.some(item => item.id === sidebarBreach.id) || (sidebarBreach.formattedId && sidebarBreach.formattedId.startsWith("AG-"));
      const endpoint = isAgent ? `/agent-breaches/${sidebarBreach.id}/send-email` : `/breaches/${sidebarBreach.id}/send-email`;

      const response = await apiFetch(endpoint, {
        method: "POST"
      });
      if (response.ok) {
        const updated = await response.json();

        // Update local list
        if (isAgent) {
          const updatedList = agentBreachesList.map((b) => {
            if (b.id === sidebarBreach.id) {
              const mapped = {
                ...b,
                emailLogs: updated.email_logs || []
              };
              setSidebarBreach(mapped);
              return mapped;
            }
            return b;
          });
          setAgentBreachesList(updatedList);
        } else {
          const updatedList = breachesList.map((b) => {
            if (b.id === sidebarBreach.id) {
              const mapped = {
                ...b,
                emailLogs: updated.email_logs || []
              };
              setSidebarBreach(mapped);
              return mapped;
            }
            return b;
          });
          setBreachesList(updatedList);
        }

        const lastLog = updated.email_logs && updated.email_logs[updated.email_logs.length - 1];
        const recipient = lastLog ? lastLog.recipient : (sidebarBreach.reportedBy || sidebarBreach.reported_by || "reporter");

        showNotification(`Alert email sent successfully to ${recipient}!`, "success");
      } else {
        const errorData = await response.json();
        console.error("Failed to send manual email alert:", errorData);
        showError(errorData.detail || "Failed to send manual email alert. Please verify organization settings.");
      }
    } catch (err) {
      console.error("Error sending email from sidebar:", err);
      showError("Error sending email from sidebar. Please try again.");
    } finally {
      setSendingEmailId(null);
    }
  };

  const validateStep1 = () => {
    try {
      const errs = {};
      if (!incidentTitle || !String(incidentTitle).trim()) errs.incidentTitle = "Incident Title is required";
      if (!reportedBy || !String(reportedBy).trim()) errs.reportedBy = "Reported By is required";
      if (!department) errs.department = "Department is required";
      if (!description || !String(description).trim()) errs.description = "Description is required";
      setFormErrors(errs);
      return Object.keys(errs).length === 0;
    } catch (e) {
      console.error("Error in validateStep1:", e);
      return false;
    }
  };

  const validateStep2 = () => {
    try {
      const errs = {};
      if (!dpsAffected) errs.dpsAffected = "Number of Data Principals Affected is required";
      if (!recordsImpacted) errs.recordsImpacted = "Estimated Records Impacted is required";
      if (dpoNotified === "Yes" && !dpoNotificationDate) {
        errs.dpoNotificationDate = "DPO Notification Date is required";
      }
      setFormErrors(errs);
      return Object.keys(errs).length === 0;
    } catch (e) {
      console.error("Error in validateStep2:", e);
      return false;
    }
  };


  useEffect(() => {
    const fetchBreaches = async () => {
      try {
        const response = await apiFetch("/breaches/");
        if (response.ok) {
          const data = await response.json();
          if (data) {
            // Sort database breaches oldest-to-newest before mapping to generate correct sequential formatted IDs
            const sorted = [...data].sort((x, y) => new Date(x.created_at || 0) - new Date(y.created_at || 0));
            const mapped = sorted.map((b, idx) => {
              const year = b.created_at ? new Date(b.created_at).getFullYear() : 2026;
              const serialStr = String(idx + 1).padStart(3, '0');
              return {
                id: b.id,
                formattedId: `BR-${year}-${serialStr}`,
                incident: b.incident_title,
                description: b.description,
                category: b.category,
                severity: b.severity,
                reportedBy: b.reported_by || b.reportedBy || "",
                department: b.department || "",
                dpsAffected: b.dps_affected ? Number(b.dps_affected).toLocaleString() : "0",
                status: b.status,
                detectionDateTime: b.detection_date_time,
                dpoNotified: b.dpo_notified,
                dpoNotificationDate: b.dpo_notification_date,
                dpbNotificationRequired: b.dpb_notification_required,
                dataPrincipalNotificationRequired: b.data_principal_notification_required,
                emailLogs: b.email_logs || [],
                rootCause: b.root_cause || "",
                affectedSystem: b.affected_system || "",
                affectedDataTypes: b.affected_data_types || [],
                recordsImpacted: b.records_impacted ? Number(b.records_impacted).toLocaleString() : "0",
                financialImpact: b.financial_impact || "",
                riskLevel: b.risk_level || b.severity || "",
                potentialImpactDesc: b.potential_impact_desc || "",
                regulatoryAuthority: b.regulatory_authority || "",
                uploadedFiles: b.uploaded_files || [],
                resolutionComment: b.resolution_comment || "",
                resolutionFiles: b.resolution_files || [],
                createdAt: b.created_at || b.detection_date_time,
                updatedAt: b.updated_at || b.created_at || b.detection_date_time,
                action: { label: "Manage", variant: "default" }
              };
            });
            // Reverse mapped array back to newest-first order for UI dashboard view
            setBreachesList(mapped.reverse());
          } else {
            setBreachesList([]);
          }
        }
      } catch (err) {
        console.error("Error loading breaches from database:", err);
      }
    };
    fetchBreaches();
    fetchAgentBreaches(1, "");
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setClockSeconds((s) => Math.max(0, s - 1));
      setNow(new Date());
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (viewingAgentBreaches) {
      fetchAgentBreaches(1, debouncedSearchQuery);
    }
  }, [viewingAgentBreaches, debouncedSearchQuery]);

  const selectedBreach =
    (viewingAgentBreaches
      ? agentBreachesList.find(b => b.id === selectedBreachId) || agentBreachesList[0]
      : breachesList.find(b => b.id === selectedBreachId) || breachesList[0]
    ) || null;
  const dynamicChecklist = selectedBreach ? getDynamicChecklist(selectedBreach, now) : [];
  const timelineSteps = selectedBreach ? getDynamicTimeline(selectedBreach, now) : [];

  const timelineDone = timelineSteps.filter(s => s.done).length;
  const timelineProgressPercent = timelineSteps.length > 0 ? (timelineDone / timelineSteps.length) * 100 : 0;

  const checklist_done = dynamicChecklist.filter((c) => c.done).length;
  const filtered = breachesList.filter((b) => {
    const matchesSeverity = severityFilter === "All Severity" || b.severity === severityFilter;

    const createdAtStr = b.createdAt ? formatFriendlyDateTime(b.createdAt) : "";
    const updatedAtStr = b.updatedAt ? formatFriendlyDateTime(b.updatedAt) : "";
    const detectionDateTimeStr = b.detectionDateTime ? formatFriendlyDateTime(b.detectionDateTime) : "";

    const matchesSearch = !searchQuery.trim() ||
      (b.formattedId || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.incident || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.category || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.status || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.reportedBy || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      createdAtStr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      updatedAtStr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      detectionDateTimeStr.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSeverity && matchesSearch;
  });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginatedBreaches = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const activeManualCount = breachesList.filter(b => b.status !== 'Closed' && b.status !== 'Remediated').length;
  const activeManualPageCount = paginatedBreaches.filter(b => b.status !== 'Closed' && b.status !== 'Remediated').length;
  const activeAgentCount = agentBreachesList.filter(b => b.status !== 'Closed' && b.status !== 'Remediated' && b.status !== 'Resolved').length;

  const activeBreachesCount = viewingAgentBreaches ? agentActiveCount : activeManualCount;
  const totalBreachesCount = viewingAgentBreaches ? agentTotalCount : breachesList.length;

  const activeCriticalOrHigh = viewingAgentBreaches
    ? agentActiveCount
    : breachesList.filter(b => (b.severity === 'Critical' || b.severity === 'High') && b.status !== 'Closed' && b.status !== 'Remediated').length;

  const notificationsPendingCount = viewingAgentBreaches
    ? agentPendingCount
    : breachesList.filter(b => b.status === 'Investigating' || b.status === 'Under Review').length;

  const resolvedCount = viewingAgentBreaches
    ? agentResolvedCount
    : breachesList.filter(b => b.status === 'Closed' || b.status === 'Remediated').length;

  const avgDetectionTimeStr = (() => {
    const list = viewingAgentBreaches ? agentBreachesList : breachesList;
    const filteredList = list.filter(b => b.detectionDateTime && b.createdAt);
    if (filteredList.length === 0) return "—";
    let totalMs = 0;
    let count = 0;
    filteredList.forEach(b => {
      const diff = new Date(b.createdAt) - new Date(b.detectionDateTime);
      // Exclude unrealistic mock data (gaps >= 30 days) to keep stats realistic
      if (diff > 0 && diff < 30 * 24 * 3600 * 1000) {
        totalMs += diff;
        count++;
      }
    });
    if (count === 0) return "—";
    const avgHrs = Math.round((totalMs / count) / (3600 * 1000));
    if (avgHrs === 0) {
      const avgMins = Math.round((totalMs / count) / (60 * 1000));
      return `${avgMins}m`;
    }
    return `${avgHrs}h`;
  })();

  const getSingleBreachExportPayload = (breach, checklist, timeline, forExcel = false) => {
    const formattedId = breach.formattedId || breach.id;
    const titleStr = `${formattedId} - Incident Report`;
    const subtitleStr = breach.incident || "Data Breach Incident";

    const metrics = [
      { label: "Data Principals Affected", value: breach.dpsAffected || "0", color: "#3b82f6" },
      { label: "Severity", value: breach.severity || "Low", color: breach.severity === "Critical" ? "#ef4444" : breach.severity === "High" ? "#f97316" : "#eab308" },
      { label: "Incident Status", value: breach.status === "Closed" ? "Resolved" : (breach.status || "Investigating"), color: breach.status === "Closed" ? "#10b981" : "#3b82f6" }
    ];

    const rows = [
      { label: "Case ID", value: formattedId },
      { label: "Incident Title", value: breach.incident || "" },
      { label: "Description", value: breach.description || "" },
      { label: "Category", value: breach.category || "" },
      { label: "Severity", value: breach.severity || "" },
      { label: "Affected Systems", value: breach.affectedSystem || "—" },
      { label: "Affected Data Types", value: breach.affectedDataTypes ? (Array.isArray(breach.affectedDataTypes) ? breach.affectedDataTypes.join(", ") : String(breach.affectedDataTypes)) : "—" },
      { label: "Data Principals Affected", value: breach.dpsAffected || "0" },
      { label: "Records Impacted", value: breach.recordsImpacted || "—" },
      { label: "Financial Impact", value: breach.financialImpact || "—" },
      { label: "Risk Level", value: breach.riskLevel || breach.severity || "" },
      { label: "Risk Description", value: breach.potentialImpactDesc || "—" },
      { label: "DPO Notified", value: breach.dpoNotified || "—" },
      { 
        label: "DPO Notification Date & Time", 
        value: breach.dpoNotificationDate 
          ? (isNaN(new Date(breach.dpoNotificationDate).getTime()) 
              ? breach.dpoNotificationDate 
              : new Date(breach.dpoNotificationDate).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + " IST")
          : "—" 
      },
      { label: "DPB Notification Required", value: breach.dpbNotificationRequired || "—" },
      { label: "Data Principal Notification Required", value: breach.dataPrincipalNotificationRequired || "—" },
      { label: "Regulatory Authority", value: breach.regulatoryAuthority || "—" }
    ];

    const qaChecklist = checklist.map((c, i) => ({
      question: `Step ${i + 1}: ${c.label}`,
      answer: c.done ? "Completed" : "Pending",
      note: c.sub || ""
    }));

    const timelineHeaders = ["Milestone", "Date", "Time", "Status"];
    const timelineRows = timeline.map(t => [
      t.label || "",
      t.date || "—",
      t.time || "—",
      t.done ? "Completed" : (t.warn ? "Overdue" : "Pending")
    ]);

    const sections = [
      {
        heading: "Executive Summary & Incident Metadata",
        metrics,
        rows
      },
      {
        heading: "Incident Details & Root Cause",
        rows: [
          { label: "Description of Incident", value: breach.description || "" },
          { label: "Root Cause Analysis", value: breach.rootCause || "—" },
          { label: "Potential Impact Details", value: breach.potentialImpactDesc || "—" }
        ]
      },
      {
        heading: "Compliance Checklist",
        qa: qaChecklist
      },
      {
        heading: "Response Timeline & Milestones",
        table: {
          headers: timelineHeaders,
          rows: timelineRows
        }
      }
    ];

    return {
      title: titleStr,
      subtitle: subtitleStr,
      module: "Breach Incident Report",
      organization: "Privacy Office / DPO",
      filename: `Breach_Report_${safeExportFilename(formattedId)}`,
      sections
    };
  };

  const getMultipleBreachesExportPayload = (list, forExcel = false) => {
    const isAgent = list.length > 0 && (list[0].formattedId || "").startsWith("AG-");
    const titleStr = isAgent ? "Agent Security Breach Register" : "Breach Incident Register";
    const subtitleStr = isAgent
      ? `EDR Security Agent Registry Overview — ${list.length} reported case(s)`
      : `Data Breach Registry Overview — ${list.length} reported case(s)`;

    // Calculate metrics
    const total = list.length;
    const criticalHigh = list.filter(b => b.severity === "Critical" || b.severity === "High").length;
    const openCount = list.filter(b => b.status !== "Closed").length;

    // Severity distributions
    const severities = ["Critical", "High", "Medium", "Low"];
    const severityCounts = severities.map(s => list.filter(b => b.severity === s).length);

    // Status distributions
    const statuses = ["Investigating", "Assessing", "Under Review", "DPB Notified", "Remediated", "Closed"];
    const statusLabels = ["Investigating", "Assessing", "Under Review", "DPB Notified", "Remediated", "Resolved"];
    const statusCounts = statuses.map(s => list.filter(b => b.status === s).length);

    const metrics = [
      { label: "Total Incidents", value: String(total), color: "#3b82f6" },
      { label: "Critical & High", value: String(criticalHigh), color: "#ef4444" },
      { label: "Active Cases", value: String(openCount), color: "#f97316" }
    ];

    const tableHeaders = ["Case ID", "Incident", "Category", "Severity", "DPs Affected", "Status", "Detection Date"];
    const tableRows = list.map(b => [
      b.formattedId || b.id,
      b.incident || "",
      b.category || "",
      b.severity || "",
      b.dpsAffected || "0",
      b.status === "Closed" ? "Resolved" : (b.status || ""),
      b.detectionDateTime ? new Date(b.detectionDateTime).toLocaleDateString("en-IN") : "—"
    ]);

    const sections = [
      {
        heading: "Incident Register Summary",
        metrics,
        charts: [
          {
            type: "donut",
            title: "Severity Distribution",
            labels: severities,
            values: severityCounts
          },
          {
            type: "pie",
            title: "Status Distribution",
            labels: statusLabels,
            values: statusCounts
          }
        ]
      },
      {
        heading: "Reported Breach Incidents",
        table: {
          headers: tableHeaders,
          rows: tableRows
        }
      }
    ];

    // For Excel, add all columns in detail
    if (forExcel) {
      const detailedHeaders = [
        "Case ID", "Incident Title", "Description", "Category", "Severity",
        "Affected Systems", "Affected Data Types", "DPs Affected", "Records Impacted",
        "Financial Impact", "Risk Level", "Risk Description", "Root Cause",
        "DPO Notified", "DPO Notification Date", "DPB Notification Required",
        "Data Principal Notification Required", "Regulatory Authority", "Status", "Detection Date"
      ];
      const detailedRows = list.map(b => [
        b.formattedId || b.id,
        b.incident || "",
        b.description || "",
        b.category || "",
        b.severity || "",
        b.affectedSystem || "—",
        b.affectedDataTypes ? (Array.isArray(b.affectedDataTypes) ? b.affectedDataTypes.join(", ") : String(b.affectedDataTypes)) : "—",
        b.dpsAffected || "0",
        b.recordsImpacted || "—",
        b.financialImpact || "—",
        b.riskLevel || b.severity || "",
        b.potentialImpactDesc || "—",
        b.rootCause || "—",
        b.dpoNotified || "—",
        b.dpoNotificationDate || "—",
        b.dpbNotificationRequired || "—",
        b.dataPrincipalNotificationRequired || "—",
        b.regulatoryAuthority || "—",
        b.status || "",
        b.detectionDateTime || ""
      ]);

      return {
        title: titleStr,
        subtitle: subtitleStr,
        module: isAgent ? "Agent Breach Register" : "Breach Register",
        organization: "Privacy Office / DPO",
        filename: isAgent ? "Agent_Breach_Register_Excel" : "Breach_Register_Excel",
        sections: [
          {
            heading: "Breach Incident Register",
            table: {
              headers: detailedHeaders,
              rows: detailedRows
            }
          }
        ]
      };
    }

    return {
      title: titleStr,
      subtitle: subtitleStr,
      module: isAgent ? "Agent Breach Register" : "Breach Register",
      organization: "Privacy Office / DPO",
      filename: isAgent ? "Agent_Breach_Register_Report" : "Breach_Register_Report",
      sections
    };
  };

  const handleExportPdf = async () => {
    if (!selectedBreach) return;
    setExportingPdf(true);
    try {
      const payload = getSingleBreachExportPayload(selectedBreach, dynamicChecklist, timelineSteps, false);
      await exportSubmissionPdf(payload);
      showNotification("PDF report generated and downloaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to export PDF report");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedBreach) return;
    setExportingExcel(true);
    try {
      const payload = getSingleBreachExportPayload(selectedBreach, dynamicChecklist, timelineSteps, true);
      await exportSubmissionExcel(payload);
      showNotification("Excel report generated and downloaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to export Excel report");
    } finally {
      setExportingExcel(false);
    }
  };

  const getTableExportList = () => {
    if (viewingAgentBreaches) {
      if (selectedBreaches.length > 0) {
        return agentBreachesList.filter(b => selectedBreaches.includes(b.id));
      }
      return agentBreachesList;
    }
    if (selectedBreaches.length > 0) {
      return breachesList.filter(b => selectedBreaches.includes(b.id));
    }
    return filtered;
  };

  const handleExportTablePdf = async () => {
    const list = getTableExportList();
    if (list.length === 0) {
      showInfo("No breach records to export.");
      return;
    }
    setExportingTablePdf(true);
    try {
      const payload = getMultipleBreachesExportPayload(list, false);
      await exportSubmissionPdf(payload);
      showNotification("Breach Register PDF generated and downloaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to export PDF registry report");
    } finally {
      setExportingTablePdf(false);
    }
  };

  const handleExportTableExcel = async () => {
    const list = getTableExportList();
    if (list.length === 0) {
      showInfo("No breach records to export.");
      return;
    }
    setExportingTableExcel(true);
    try {
      const payload = getMultipleBreachesExportPayload(list, true);
      await exportSubmissionExcel(payload);
      showNotification("Breach Register Excel generated and downloaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to export Excel registry report");
    } finally {
      setExportingTableExcel(false);
    }
  };

  const inputCls = cls(
    "w-full px-3.5 py-2.5 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all shadow-sm font-medium",
    dark ? "bg-slate-900 border-slate-700 text-slate-100 focus:border-slate-600" : "bg-white border-slate-200 text-slate-800 focus:border-slate-300"
  );

  const getDpbDueBy = () => {
    if (!detectionDateTime) return "—";
    try {
      const date = new Date(detectionDateTime);
      date.setHours(date.getHours() + 72);
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const d = date.getDate();
      const m = months[date.getMonth()];
      const y = date.getFullYear();
      const hr = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${d}-${m}-${y} ${hr}:${min} (72h Deadline)`;
    } catch {
      return "72h after detection";
    }
  };

  const handleDataTypeToggle = (type) => {
    if (affectedDataTypes.includes(type)) {
      setAffectedDataTypes(affectedDataTypes.filter((t) => t !== type));
    } else {
      setAffectedDataTypes([...affectedDataTypes, type]);
    }
  };

  const handleMockFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg";
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      const newFiles = files.map(file => file.name);
      setUploadedFiles(prev => {
        const updated = [...prev];
        newFiles.forEach(name => {
          if (!updated.includes(name)) {
            updated.push(name);
          }
        });
        return updated;
      });
    };
    input.click();
  };

  const handleFileDelete = (file) => {
    setUploadedFiles(uploadedFiles.filter((f) => f !== file));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep1() || !validateStep2()) {
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      incident_title: incidentTitle || "Logged Breach Incident",
      category: category,
      severity: severity,
      detection_date_time: detectionDateTime ? new Date(detectionDateTime).toISOString() : new Date().toISOString(),
      reported_by: reportedBy,
      department: department,
      description: description || "No description provided",
      root_cause: rootCause || null,
      affected_system: affectedSystem || null,
      affected_data_types: affectedDataTypes,
      dps_affected: dpsAffected ? Number(dpsAffected) : 0,
      records_impacted: recordsImpacted ? Number(recordsImpacted) : 0,
      financial_impact: financialImpact || null,
      risk_level: riskLevel,
      potential_impact_desc: potentialImpactDesc || null,
      dpo_notified: dpoNotified,
      dpb_notification_required: dpbNotificationRequired,
      dpo_notification_date: dpoNotificationDate ? new Date(dpoNotificationDate).toISOString() : null,
      data_principal_notification_required: dataPrincipalNotificationRequired,
      regulatory_authority: regulatoryAuthority || null,
      uploaded_files: uploadedFiles
    };

    try {
      if (editBreachId) {
        const response = await apiFetch(`/breaches/${editBreachId}`, {
          method: "PATCH",
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const saved = await response.json();

          let updatedBreachObj = null;

          const updatedList = breachesList.map((b) => {
            if (b.id === editBreachId) {
              const updatedObj = {
                ...b,
                incident: saved.incident_title,
                description: saved.description,
                category: saved.category,
                severity: saved.severity,
                reportedBy: saved.reported_by || saved.reportedBy || "",
                department: saved.department || "",
                dpsAffected: saved.dps_affected ? Number(saved.dps_affected).toLocaleString() : "0",
                status: saved.status,
                detectionDateTime: saved.detection_date_time,
                dpoNotified: saved.dpo_notified,
                dpoNotificationDate: saved.dpo_notification_date,
                dpbNotificationRequired: saved.dpb_notification_required,
                dataPrincipalNotificationRequired: saved.data_principal_notification_required,
                emailLogs: saved.email_logs || [],
                rootCause: saved.root_cause || "",
                affectedSystem: saved.affected_system || "",
                affectedDataTypes: saved.affected_data_types || [],
                recordsImpacted: saved.records_impacted ? Number(saved.records_impacted).toLocaleString() : "0",
                financialImpact: saved.financial_impact || "",
                riskLevel: saved.risk_level || saved.severity || "",
                potentialImpactDesc: saved.potential_impact_desc || "",
                regulatoryAuthority: saved.regulatory_authority || "",
                uploadedFiles: saved.uploaded_files || [],
                resolutionComment: saved.resolution_comment || "",
                resolutionFiles: saved.resolution_files || [],
                updatedAt: saved.updated_at || saved.created_at || saved.detection_date_time
              };
              updatedBreachObj = updatedObj;
              return updatedObj;
            }
            return b;
          });

          setBreachesList(updatedList);

          if (sidebarBreach && sidebarBreach.id === editBreachId && updatedBreachObj) {
            setSidebarBreach(updatedBreachObj);
            // Refresh edit logs from new dedicated table
            try {
              const logRes = await apiFetch(`/breaches/${editBreachId}/edit-logs`);
              if (logRes.ok) {
                const freshLogs = await logRes.json();
                setEditLogs(freshLogs);
              }
            } catch (err) {
              console.error("Failed to refresh edit logs:", err);
            }
          }

          showNotification("Breach ticket updated successfully!", "success");
          setShowLogForm(false);
          setWizardStep(1);
          setFormErrors({});
          setEditBreachId(null);

          setIncidentTitle("");
          setReportedBy("");
          setDescription("");
          setRootCause("");
          setAffectedSystem("");
          setDpsAffected("");
          setRecordsImpacted("");
          setFinancialImpact("");
          setPotentialImpactDesc("");
          setRegulatoryAuthority("");
          setUploadedFiles([]);
          const local = new Date();
          const offset = local.getTimezoneOffset();
          const localDate = new Date(local.getTime() - (offset * 60 * 1000));
          setDetectionDateTime(localDate.toISOString().slice(0, 16));
          setDpoNotificationDate(localDate.toISOString().slice(0, 16));
        } else {
          const errorData = await response.json();
          console.error("Failed to update breach in database:", errorData);
          showError("Failed to update breach in database. Please check all fields.");
        }
      } else {
        const response = await apiFetch("/breaches/", {
          method: "POST",
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const saved = await response.json();

          // Generate formatted sequential ID based on total breaches list size
          const nextSerial = breachesList.length + 1;
          const year = saved.created_at ? new Date(saved.created_at).getFullYear() : 2026;
          const formattedId = `BR-${year}-${String(nextSerial).padStart(3, '0')}`;

          // Map backend response object to frontend schema
          const mappedBreach = {
            id: saved.id,
            formattedId: formattedId,
            incident: saved.incident_title,
            description: saved.description,
            category: saved.category,
            severity: saved.severity,
            reportedBy: saved.reported_by || saved.reportedBy || "",
            department: saved.department || "",
            dpsAffected: saved.dps_affected ? Number(saved.dps_affected).toLocaleString() : "0",
            status: saved.status,
            detectionDateTime: saved.detection_date_time,
            dpoNotified: saved.dpo_notified,
            dpoNotificationDate: saved.dpo_notification_date,
            dpbNotificationRequired: saved.dpb_notification_required,
            dataPrincipalNotificationRequired: saved.data_principal_notification_required,
            emailLogs: saved.email_logs || [],
            rootCause: saved.root_cause || "",
            affectedSystem: saved.affected_system || "",
            affectedDataTypes: saved.affected_data_types || [],
            recordsImpacted: saved.records_impacted ? Number(saved.records_impacted).toLocaleString() : "0",
            financialImpact: saved.financial_impact || "",
            riskLevel: saved.risk_level || saved.severity || "",
            potentialImpactDesc: saved.potential_impact_desc || "",
            regulatoryAuthority: saved.regulatory_authority || "",
            uploadedFiles: saved.uploaded_files || [],
            resolutionComment: saved.resolution_comment || "",
            resolutionFiles: saved.resolution_files || [],
            createdAt: saved.created_at || saved.detection_date_time,
            updatedAt: saved.updated_at || saved.created_at || saved.detection_date_time,
            action: { label: "Manage", variant: "default" }
          };

          setBreachesList([mappedBreach, ...breachesList]);
          setSelectedBreachId(saved.id);
          showNotification("Breach ticket logged successfully!", "success");
          setShowLogForm(false);
          setWizardStep(1);
          setFormErrors({});

          setIncidentTitle("");
          setReportedBy("");
          setDescription("");
          setRootCause("");
          setAffectedSystem("");
          setDpsAffected("");
          setRecordsImpacted("");
          setFinancialImpact("");
          setPotentialImpactDesc("");
          setRegulatoryAuthority("");
          setUploadedFiles([]);
          const local = new Date();
          const offset = local.getTimezoneOffset();
          const localDate = new Date(local.getTime() - (offset * 60 * 1000));
          setDetectionDateTime(localDate.toISOString().slice(0, 16));
          setDpoNotificationDate(localDate.toISOString().slice(0, 16));
        } else {
          const errorData = await response.json();
          console.error("Failed to save breach to database:", errorData);
          showError("Failed to save breach to database. Please check all fields.");
        }
      }
    } catch (err) {
      console.error("Error connecting to database API:", err);
      showError("Error connecting to database API. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className={cls(
          "min-h-screen -m-4 lg:-m-6 transition-colors duration-300 px-4 sm:px-5 lg:px-6 py-6 space-y-6",
          dark ? "bg-[#0b0f19] text-slate-100" : "bg-slate-55 text-slate-900"
        )}
        style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
      >
      <AnimatePresence mode="wait">
        {!showLogForm ? (
          /* ════════════════════════════════════════════════════════════════
             DASHBOARD VIEW
             ════════════════════════════════════════════════════════════════ */
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-5" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <div
                    className="absolute inset-0 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 blur-md opacity-35 scale-110"
                    aria-hidden
                  />
                  <div
                    className="relative w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-rose-500/20 ring-1 ring-white/40"
                    style={{ background: "linear-gradient(135deg, #f43f5e 0%, #be123c 100%)" }}
                  >
                    <ShieldAlert size={18} className="text-white drop-shadow-sm" />
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold tracking-tight font-display" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Breach Management
                  </h1>
                  <p className={cls("text-sm mt-1", dark ? "text-slate-400" : "text-slate-500")}>
                    Track, assess, and report data breaches under DPDP Act 2023 compliance.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Export Report Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowTopExportMenu(!showTopExportMenu)}
                    disabled={!selectedBreach || exportingPdf || exportingExcel}
                    data-tour-id="tour-breach-export"
                    className={cls(
                      "px-4 py-2.5 rounded-xl text-sm font-semibold border flex items-center gap-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                      dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55"
                    )}
                  >
                    {(exportingPdf || exportingExcel) ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Download size={15} />
                    )}
                    <span>{(exportingPdf || exportingExcel) ? "Exporting..." : "Export Report"}</span>
                    <ChevronDown size={14} className={cls("transition-transform duration-200", showTopExportMenu ? "rotate-180" : "")} />
                  </button>

                  {showTopExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTopExportMenu(false)} />
                      <div className={cls(
                        "absolute right-0 mt-2 w-48 rounded-xl border shadow-lg z-20 py-1.5 overflow-hidden",
                        dark ? "bg-slate-800 border-slate-700 text-slate-200" : "bg-white border-slate-100 text-slate-700"
                      )}>
                        <button
                          onClick={() => { handleExportPdf(); setShowTopExportMenu(false); }}
                          disabled={exportingPdf}
                          className={cls(
                            "w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-slate-50 transition-colors disabled:opacity-50",
                            dark ? "hover:bg-slate-705 text-slate-200" : "hover:bg-slate-50 text-slate-700"
                          )}
                        >
                          <FileDown size={14} className="text-blue-500" />
                          <span>{exportingPdf ? "Generating PDF..." : "Export PDF"}</span>
                        </button>
                        <button
                          onClick={() => { handleExportExcel(); setShowTopExportMenu(false); }}
                          disabled={exportingExcel}
                          className={cls(
                            "w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-slate-50 transition-colors disabled:opacity-50",
                            dark ? "hover:bg-slate-705 text-slate-200" : "hover:bg-slate-50 text-slate-700"
                          )}
                        >
                          <FileSpreadsheet size={14} className="text-emerald-500" />
                          <span>{exportingExcel ? "Generating Excel..." : "Export Excel"}</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Log New Breach */}
                <button
                  onClick={handleNewBreachClick}
                  data-tour-id="tour-breach-report"
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
                >
                  <Plus size={16} />
                  <span>Log New Breach</span>
                </button>
              </div>
            </div>

            {/* Dynamic Case Banner */}
            {selectedBreach && (
              <div
                className={cls(
                  "rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 border transition-all duration-300 shadow-sm",
                  ["Closed", "Remediated"].includes(selectedBreach.status)
                    ? (dark ? "bg-emerald-950/20 border-emerald-900/60 text-emerald-200" : "bg-[#f0fdf4] border-emerald-100 text-emerald-950")
                    : selectedBreach.status === "DPB Notified"
                      ? (dark ? "bg-blue-955/20 border-blue-900/60 text-blue-200" : "bg-[#eff6ff] border-blue-100 text-blue-950")
                      : ["Critical", "High"].includes(selectedBreach.severity)
                        ? (dark ? "bg-red-950/20 border-red-900/60 text-red-200" : "bg-[#fff1f2] border-red-100 text-red-950")
                        : (dark ? "bg-amber-950/20 border-amber-900/60 text-amber-200" : "bg-[#fffbeb] border-amber-100 text-amber-950")
                )}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={cls(
                    "w-16 h-16 rounded-full border flex items-center justify-center flex-shrink-0 relative",
                    ["Closed", "Remediated"].includes(selectedBreach.status)
                      ? (dark ? "bg-emerald-900/20 border-emerald-800" : "bg-emerald-100 border-emerald-200")
                      : selectedBreach.status === "DPB Notified"
                        ? (dark ? "bg-blue-900/20 border-blue-800" : "bg-blue-100 border-blue-200")
                        : ["Critical", "High"].includes(selectedBreach.severity)
                          ? (dark ? "bg-red-900/20 border-red-800" : "bg-red-100 border-red-200")
                          : (dark ? "bg-amber-900/20 border-amber-800" : "bg-amber-100 border-amber-200")
                  )}>
                    <div className={cls(
                      "w-11 h-11 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md",
                      ["Closed", "Remediated"].includes(selectedBreach.status)
                        ? "bg-emerald-500"
                        : selectedBreach.status === "DPB Notified"
                          ? "bg-blue-500"
                          : ["Critical", "High"].includes(selectedBreach.severity)
                            ? "bg-red-500"
                            : "bg-amber-500"
                    )}>
                      {["Closed", "Remediated"].includes(selectedBreach.status) ? "✓" : "!"}
                    </div>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className={cls(
                      "text-xs font-black tracking-widest uppercase",
                      ["Closed", "Remediated"].includes(selectedBreach.status)
                        ? "text-emerald-600"
                        : selectedBreach.status === "DPB Notified"
                          ? "text-blue-600"
                          : ["Critical", "High"].includes(selectedBreach.severity)
                            ? "text-red-650"
                            : "text-amber-600"
                    )}>
                      {["Closed", "Remediated"].includes(selectedBreach.status)
                        ? "RESOLVED"
                        : selectedBreach.status === "DPB Notified"
                          ? "DPB NOTIFIED"
                          : "ACTIVE BREACH"}{" "}
                      · {selectedBreach.formattedId}
                    </span>
                    <div className={cls("text-base font-extrabold mt-1 break-all", dark ? "text-slate-100" : "text-slate-805")}>
                      {selectedBreach.incident}
                    </div>
                    <span className="text-xs text-slate-505 mt-1 font-semibold">
                      {["Closed", "Remediated"].includes(selectedBreach.status) ? (
                        "Case is fully resolved and closed under DPDP guidelines."
                      ) : selectedBreach.status === "DPB Notified" ? (
                        "Regulatory reporting has been filed with the Data Protection Board."
                      ) : (
                        <>
                          DPDP  notification to DPB due in{" "}
                          <span className={cls(
                            "font-extrabold",
                            ["Critical", "High"].includes(selectedBreach.severity) ? "text-red-600" : "text-amber-600"
                          )}>
                            {getDpbClock(selectedBreach.detectionDateTime, selectedBreach.dpoNotified, now, selectedBreach.status, selectedBreach.updatedAt).label}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Circular Progress Ring or Status indicator */}
                <div className="flex items-center gap-6 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="relative w-14 h-14 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          className={dark ? "text-slate-850" : "text-slate-100"}
                          strokeWidth="3.5"
                          stroke="currentColor"
                          fill="none"
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{ stroke: dark ? "#1f2937" : "#e2e8f0" }}
                        />
                        <path
                          className={cls(
                            ["Closed", "Remediated"].includes(selectedBreach.status)
                              ? "text-emerald-500"
                              : selectedBreach.status === "DPB Notified"
                                ? "text-blue-500"
                                : ["Critical", "High"].includes(selectedBreach.severity)
                                  ? "text-red-500"
                                  : "text-amber-500"
                          )}
                          strokeDasharray={`${Math.round((checklist_done / dynamicChecklist.length) * 100)}, 100`}
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          stroke="currentColor"
                          fill="none"
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-black text-slate-700" style={{ color: dark ? "#fca5a5" : "#0f172a" }}>
                        {Math.round((checklist_done / dynamicChecklist.length) * 100)}%
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold tracking-widest text-slate-400 flex items-center gap-1 uppercase select-none">
                        COMPLIANCE <Info size={11} className="text-slate-400 cursor-pointer" />
                      </span>
                      <span className={cls(
                        "text-lg font-black leading-none mt-1",
                        ["Closed", "Remediated"].includes(selectedBreach.status)
                          ? "text-emerald-600"
                          : selectedBreach.status === "DPB Notified"
                            ? "text-blue-600"
                            : ["Critical", "High"].includes(selectedBreach.severity)
                              ? "text-red-650"
                              : "text-amber-600"
                      )}>
                        {checklist_done}/{dynamicChecklist.length}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 mt-0.5 select-none">tasks done</span>
                    </div>
                  </div>

                  {/* Take Action / View Action */}
                  {!["Closed", "Remediated", "DPB Notified"].includes(selectedBreach.status) && (
                    <button
                      onClick={() => handleEditBreachClick(selectedBreach)}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:opacity-95 transition-all shadow-sm hover:shadow"
                    >
                      Take Action →
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-tour-id="tour-breach-stats">
              <StatCard dark={dark} label="TOTAL BREACHES" value={totalBreachesCount} color="indigo" sub="Total tracked incidents" icon={Shield} />
              <StatCard dark={dark} label="ACTIVE BREACHES" value={activeBreachesCount} color="red" sub={`${activeCriticalOrHigh} require immediate action`} icon={Bell} />
              <StatCard dark={dark} label="NOTIFICATIONS PENDING" value={notificationsPendingCount} color="amber" sub={`${notificationsPendingCount} pending review`} icon={Bell} />
              <StatCard dark={dark} label="AVG. DETECTION TIME" value={avgDetectionTimeStr} color="blue" sub="FY2024 benchmark: 24h" icon={Clock} />
              <StatCard dark={dark} label="RESOLVED (90 DAYS)" value={resolvedCount} color="emerald" sub="100% within DPDP limits" icon={CheckCircle2} />
            </div>

            {/* Timeline */}
            <div className={cls("rounded-2xl p-6 border transition-all duration-300 shadow-sm", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60 hover:border-slate-300 hover:shadow-md")}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 border-b pb-4" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                <div className="flex items-center gap-2.5">
                  <span className={cls("text-xs font-black tracking-wider uppercase", dark ? "text-slate-205" : "text-slate-800")}>DPDP RESPONSE TIMELINE</span>
                  {selectedBreach ? (
                    <span className={cls("px-2.5 py-0.5 rounded-full text-[10px] font-bold border", dark ? "bg-blue-955/40 border-blue-900 text-blue-400" : "bg-blue-50 border-blue-100 text-blue-600")}>
                      {selectedBreach.formattedId}
                    </span>
                  ) : (
                    <span className={cls("px-2.5 py-0.5 rounded-full text-[10px] font-bold border", dark ? "bg-slate-900 border-slate-800 text-slate-400" : "bg-slate-50 border-slate-200 text-slate-500")}>
                      NO ACTIVE CASE
                    </span>
                  )}
                </div>
                {selectedBreach && (
                  <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1">
                    <Calendar size={12} />
                    Detection: {selectedBreach.detectionDateTime ? new Date(selectedBreach.detectionDateTime).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : "—"} IST
                  </span>
                )}
              </div>

              {/* Timeline steps */}
              <div className={cls("grid grid-cols-2 gap-y-6 md:gap-y-0 gap-x-2 relative mt-4", timelineSteps.length === 4 ? "md:grid-cols-4" : timelineSteps.length === 5 ? "md:grid-cols-5" : timelineSteps.length === 6 ? "md:grid-cols-6" : "md:grid-cols-7")}>
                {timelineSteps.length === 0 ? (
                  <p className="col-span-full py-4 text-center text-xs font-bold text-slate-400 italic">No timeline steps to display.</p>
                ) : (
                  timelineSteps.map((step, index) => {
                    const isLast = index === timelineSteps.length - 1;
                    let dueDateStr = "72h";
                    if (selectedBreach && selectedBreach.detectionDateTime) {
                      const date = new Date(selectedBreach.detectionDateTime);
                      date.setHours(date.getHours() + 72);
                      dueDateStr = `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
                    }
                    return (
                      <div key={index} className="flex flex-col items-center relative min-w-0">
                        {/* Connector Line (Desktop Only) */}
                        {!isLast && (
                          <div
                            className="hidden md:block absolute top-[15px] left-1/2 w-full h-0.5 z-0"
                            style={{
                              background: step.done
                                ? (dark ? "#10b981" : "#22c55e")
                                : (dark ? "#2d3f5e" : "#e2e8f0")
                            }}
                          />
                        )}

                        {/* Node Circle */}
                        <div className="relative flex items-center justify-center w-8 h-8 z-10">
                          <div
                            className={cls(
                              "w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all shadow-sm",
                              step.done
                                ? "bg-emerald-500 border-emerald-500 text-white"
                                : step.warn
                                  ? (dark ? "bg-amber-955 border-amber-500 text-amber-400" : "bg-amber-55 border-amber-400 text-amber-600")
                                  : (dark ? "bg-slate-900 border-slate-800 text-slate-605" : "bg-white border-slate-205 text-slate-400")
                            )}
                          >
                            {step.done ? (
                              <Check size={14} strokeWidth={3} />
                            ) : step.warn ? (
                              <span className="font-black text-sm">!</span>
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-current" />
                            )}
                          </div>
                        </div>

                        {/* Text labels */}
                        <div className="mt-3 text-center px-1 z-10">
                          <p
                            className={cls(
                              "text-xs font-bold leading-tight",
                              step.done
                                ? (dark ? "text-slate-200" : "text-slate-700")
                                : step.warn
                                  ? "text-amber-600 font-extrabold"
                                  : "text-slate-400"
                            )}
                          >
                            {step.label}
                          </p>
                          {step.date ? (
                            <p className="text-[10px] text-slate-450 mt-1 font-semibold">
                              {step.date} · {step.time}
                            </p>
                          ) : step.sub ? (
                            <p className="text-[10px] text-slate-450 mt-1 italic">
                              {step.sub}
                            </p>
                          ) : null}
                          {step.due && selectedBreach && (
                            <p className="text-[10px] font-extrabold text-amber-500 mt-1">
                              Due: {dueDateStr}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Timeline progress gradient bar */}
              <div className="w-full h-1.5 rounded-full relative overflow-hidden mt-6 bg-slate-100" style={{ background: dark ? "#1e293b" : "#f1f5f9" }}>
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-yellow-500 to-orange-500" style={{ width: timelineProgressPercent + "%" }} />
              </div>

              {/* Legal Warning Alert */}
              <div
                className={cls(
                  "mt-6 rounded-xl p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300",
                  dark ? "bg-amber-950/15 border-amber-900/40 text-amber-250/90" : "bg-[#fffbeb] border-amber-200 text-amber-900"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cls("w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm border", dark ? "bg-amber-900/30 border-amber-800 text-amber-400" : "bg-amber-100 border-amber-300 text-amber-700")}>
                    <Scale size={14} />
                  </div>
                  <p className="text-xs leading-relaxed font-medium">
                    <span className="font-extrabold text-amber-600">DPDP Act 2023 (1)</span>
                    {" — A Data Fiduciary must notify the Data Protection Board and each affected Data Principal of a personal data breach in such manner and within such time period as may be prescribed. Failure carries penalty up to "}
                    <span className="font-extrabold text-red-650">₹250 Crore.</span>
                  </p>
                </div>
                <button
                  className={cls(
                    "px-4 py-2 rounded-xl text-xs font-bold border flex items-center gap-1.5 flex-shrink-0 transition-all shadow-sm hover:scale-[1.02] active:scale-[0.98]",
                    dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : "bg-white border-slate-200 text-slate-750 hover:bg-slate-55"
                  )}
                >
                  <span>View Act Section</span>
                  <span className="text-[10px]">↗</span>
                </button>
              </div>
            </div>

            {/* Active Breach Register Table */}
            <div data-tour-id="tour-breach-log">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-6">
                  <button
                    type="button"
                    onClick={() => {
                      setViewingAgentBreaches(false);
                      setSelectedBreachId(null);
                      setSelectedBreaches([]);
                    }}
                    className={cls(
                      "text-sm font-black tracking-wider uppercase font-display pb-1 border-b-2 transition-all duration-200 flex items-center gap-2",
                      !viewingAgentBreaches
                        ? (dark ? "text-red-400 border-red-500" : "text-slate-800 border-red-500")
                        : (dark ? "text-slate-400 border-transparent hover:text-slate-200" : "text-slate-550 border-transparent hover:text-slate-800")
                    )}
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    <span>ACTIVE BREACH REGISTER</span>
                    {activeManualPageCount > 0 && (
                      <span className={cls(
                        "px-1.5 py-0.5 rounded-full text-[10px] font-black leading-none",
                        !viewingAgentBreaches
                          ? "bg-red-500 text-white"
                          : (dark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500")
                      )}>
                        {activeManualPageCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewingAgentBreaches(true);
                      setSelectedBreachId(null);
                      setSelectedBreaches([]);
                    }}
                    className={cls(
                      "text-sm font-black tracking-wider uppercase font-display pb-1 border-b-2 transition-all duration-200 flex items-center gap-2",
                      viewingAgentBreaches
                        ? (dark ? "text-red-400 border-red-500" : "text-slate-800 border-red-500")
                        : (dark ? "text-slate-400 border-transparent hover:text-slate-200" : "text-slate-550 border-transparent hover:text-slate-800")
                    )}
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    <span>AGENT BREACHES</span>
                    {activeAgentCount > 0 && (
                      <span className={cls(
                        "px-1.5 py-0.5 rounded-full text-[10px] font-black leading-none animate-pulse",
                        viewingAgentBreaches
                          ? "bg-red-500 text-white"
                          : (dark ? "bg-slate-800 text-red-400" : "bg-red-50 text-red-650 border border-red-100")
                      )}>
                        {activeAgentCount}
                      </span>
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search breaches..."
                      className={cls(
                        "pl-9 pr-4 py-2 rounded-xl text-xs font-bold border focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all shadow-sm w-44 sm:w-56",
                        dark ? "bg-slate-800 border-slate-700 text-slate-200 focus:border-slate-600" : "bg-white border-slate-200 text-slate-700 focus:border-slate-300"
                      )}
                    />
                  </div>

                  {/* Export Table Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowTableExportMenu(!showTableExportMenu)}
                      disabled={(viewingAgentBreaches ? agentBreachesList.length === 0 : breachesList.length === 0) || exportingTablePdf || exportingTableExcel}
                      className={cls(
                        "px-3.5 py-2 rounded-xl text-xs font-bold border flex items-center gap-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                        selectedBreaches.length > 0
                          ? "bg-gradient-to-r from-red-500 to-orange-500 border-red-400 text-white hover:opacity-95 shadow-md scale-[1.05]"
                          : (dark ? "bg-slate-800 border-slate-700 text-slate-350 hover:bg-slate-750" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55")
                      )}
                    >
                      {(exportingTablePdf || exportingTableExcel) ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Download size={13} />
                      )}
                      <span>{(exportingTablePdf || exportingTableExcel) ? "Exporting..." : "Export"}</span>
                      <ChevronDown size={12} className={cls("transition-transform duration-200", showTableExportMenu ? "rotate-180" : "")} />
                    </button>

                    {showTableExportMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowTableExportMenu(false)} />
                        <div className={cls(
                          "absolute right-0 mt-2 w-44 rounded-xl border shadow-lg z-20 py-1.5 overflow-hidden",
                          dark ? "bg-slate-800 border-slate-700 text-slate-200" : "bg-white border-slate-100 text-slate-700"
                        )}>
                          <button
                            onClick={() => { handleExportTablePdf(); setShowTableExportMenu(false); }}
                            disabled={exportingTablePdf}
                            className={cls(
                              "w-full text-left px-3.5 py-2 text-xs flex items-center gap-2 transition-colors disabled:opacity-50",
                              dark ? "hover:bg-slate-705 text-slate-200" : "hover:bg-slate-50 text-slate-700"
                            )}
                          >
                            <FileDown size={13} className="text-blue-500" />
                            <span>{exportingTablePdf ? "Generating PDF..." : "Export as PDF"}</span>
                          </button>
                          <button
                            onClick={() => { handleExportTableExcel(); setShowTableExportMenu(false); }}
                            disabled={exportingTableExcel}
                            className={cls(
                              "w-full text-left px-3.5 py-2 text-xs flex items-center gap-2 transition-colors disabled:opacity-50",
                              dark ? "hover:bg-slate-705 text-slate-200" : "hover:bg-slate-50 text-slate-700"
                            )}
                          >
                            <FileSpreadsheet size={13} className="text-emerald-500" />
                            <span>{exportingTableExcel ? "Generating Excel..." : "Export as Excel"}</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="relative" data-tour-id="tour-breach-filter">
                    <Filter size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <select
                      value={severityFilter}
                      onChange={(e) => {
                        setSeverityFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className={cls(
                        "pl-9 pr-8 py-2 rounded-xl text-xs font-bold border appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer shadow-sm",
                        dark ? "bg-slate-800 border-slate-700 text-slate-200" : "bg-white border-slate-200 text-slate-700"
                      )}
                      style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 10px center', backgroundSize: '12px', backgroundRepeat: 'no-repeat' }}
                    >
                      {["All Severity", "Critical", "High", "Medium", "Low"].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className={cls("rounded-2xl border shadow-sm transition-all duration-300 overflow-hidden", dark ? "border-slate-805 hover:border-slate-700 bg-[#0f1117]" : "border-slate-200/60 hover:border-slate-300 bg-white")}>
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-left border-collapse">
                    {viewingAgentBreaches ? (
                      <>
                        <thead>
                          <tr
                            className={cls(
                              "border-b text-[10px] font-extrabold tracking-widest uppercase",
                              dark ? "bg-[#141927] border-slate-800 text-slate-400" : "bg-slate-55 border-slate-200 text-slate-500"
                            )}
                          >
                            <th className="py-3.5 px-4 select-none text-center w-10">
                               <input
                                 type="checkbox"
                                 checked={agentBreachesList.length > 0 && agentBreachesList.every((b) => selectedBreaches.includes(b.id))}
                                 onChange={(e) => {
                                   if (e.target.checked) {
                                     const toAdd = agentBreachesList.filter(b => !selectedBreaches.includes(b.id)).map(b => b.id);
                                     setSelectedBreaches([...selectedBreaches, ...toAdd]);
                                   } else {
                                     const pageIds = agentBreachesList.map(b => b.id);
                                     setSelectedBreaches(selectedBreaches.filter(id => !pageIds.includes(id)));
                                   }
                                 }}
                                 className={cls("rounded focus:ring-blue-500/40 cursor-pointer w-3.5 h-3.5", dark ? "bg-slate-900 border-slate-700 text-blue-500" : "bg-white border-slate-300 text-blue-655")}
                               />
                             </th>
                            <th className="py-3.5 px-5 select-none text-center w-14 whitespace-nowrap">S.No</th>
                            <th className="py-3.5 px-5 select-none whitespace-nowrap">ID</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Created At</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Updated On</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Source</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Sender</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">To</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Subject</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Label</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Display</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Body Text</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Status</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">DPB Clock</th>
                            <th className="py-3.5 px-5 select-none text-center whitespace-nowrap">Actions</th>
                          </tr>
                        </thead>
                        <tbody className={cls("divide-y", dark ? "divide-slate-800 bg-[#0f1117]" : "divide-slate-100 bg-white")}>
                          {agentLoading ? (
                            <tr>
                              <td colSpan={15} className="py-8 text-center text-xs font-bold text-slate-400">
                                Loading Agent Breaches...
                              </td>
                            </tr>
                          ) : agentBreachesList.length === 0 ? (
                            <tr>
                              <td colSpan={15} className="py-8 text-center text-xs font-bold text-slate-400 italic">
                                No agent breach records found.
                              </td>
                            </tr>
                          ) : (
                            agentBreachesList.map((item, idx) => (
                              <tr
                                key={item.id}
                                onClick={() => setSelectedBreachId(item.id)}
                                className={cls(
                                  "transition-all duration-150 cursor-pointer border-l-2",
                                  selectedBreach && selectedBreach.id === item.id
                                    ? (dark ? "bg-slate-800/40 border-l-red-500" : "bg-blue-50/40 border-l-red-500")
                                    : (dark ? "border-l-transparent hover:bg-slate-850/30" : "border-l-transparent hover:bg-slate-55/50")
                                )}
                              >
                                <td className="py-4 px-4 text-center w-10" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedBreaches.includes(item.id)}
                                    onChange={() => handleSelectRow(item.id)}
                                    className={cls("rounded focus:ring-blue-500/40 cursor-pointer w-3.5 h-3.5", dark ? "bg-slate-900 border-slate-700 text-blue-500" : "bg-white border-slate-300 text-blue-655")}
                                  />
                                </td>
                                <td className="py-4 px-5 text-xs font-bold text-center text-slate-550 whitespace-nowrap">{(agentCurrentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                <td className="py-4 px-5 whitespace-nowrap">
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleIdClick(item);
                                      }}
                                      className="text-red-500 hover:text-red-650 hover:underline font-extrabold text-sm text-left transition-all duration-150"
                                    >
                                      {item.formattedId}
                                    </button>
                                    {item.status !== "Resolved" && item.status !== "Closed" && item.status !== "Remediated" && (
                                      <Flag size={12} className="text-red-500 fill-red-500 animate-pulse flex-shrink-0" title="Unresolved Alert" />
                                    )}
                                  </div>
                                </td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{formatFriendlyDateTime(item.created_at)}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{formatFriendlyDateTime(item.updated_at)}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{item.source || "—"}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{item.sender_email || "—"}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{item.to || "—"}</td>
                                <td
                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}
                                  onClick={() => setSelectedTextModal({ title: "Subject", content: item.subject })}
                                >
                                  {item.subject && item.subject.length > 20 ? item.subject.substring(0, 20) + "..." : (item.subject || "—")}
                                </td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{item.label_name || "—"}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{item.display || "—"}</td>
                                <td
                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}
                                  onClick={() => setSelectedTextModal({ title: "Body Text", content: item.body_text })}
                                >
                                  {item.body_text && item.body_text.length > 25 ? item.body_text.substring(0, 25) + "..." : (item.body_text || "—")}
                                </td>
                                <td className="py-4 px-4 whitespace-nowrap">
                                  <StatusBadge status={item.status || "Investigating"} dark={dark} />
                                </td>
                                <td className="py-4 px-4 whitespace-nowrap">
                                  <ClockBadge dpbClock={getDpbClock(item.created_at, "Yes", now, item.status || "Investigating", item.updated_at)} dark={dark} />
                                </td>
                                <td className="py-4 px-5">
                                  <div className="flex items-center justify-center gap-2">
                                    {/* Mail Alert Action Button */}
                                    <button
                                      type="button"
                                      title="Send Email Alert"
                                      onClick={() => handleSendAgentBreachEmail(item.id)}
                                      disabled={sendingEmailId === item.id}
                                      className={cls(
                                        "p-1.5 rounded-lg border transition-all shadow-sm hover:scale-[1.05] active:scale-[0.95]",
                                        sendingEmailId === item.id ? "opacity-50 cursor-wait" : "cursor-pointer",
                                        dark
                                          ? "border-slate-800 bg-slate-900 text-blue-400 hover:bg-slate-800 hover:text-blue-300"
                                          : "border-slate-200 bg-white text-blue-600 hover:bg-slate-50 hover:text-blue-700"
                                      )}
                                    >
                                      {sendingEmailId === item.id ? (
                                        <div className="w-3.5 h-3.5 border-2 border-t-transparent border-blue-500 rounded-full animate-spin" />
                                      ) : (
                                        <Mail size={13.5} />
                                      )}
                                    </button>

                                    {/* Resolve Action Button */}
                                    <button
                                      type="button"
                                      title={["Closed", "Remediated"].includes(item.status) ? "Resolved" : "Mark as Resolved"}
                                      disabled={["Closed", "Remediated"].includes(item.status)}
                                      onClick={() => {
                                        setResolveBreachId(item.id);
                                        setResolveComment("");
                                        setResolveFiles([]);
                                        setShowResolveModal(true);
                                      }}
                                      className={cls(
                                        "p-1.5 rounded-lg border transition-all shadow-sm",
                                        ["Closed", "Remediated"].includes(item.status)
                                          ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-500 opacity-80 cursor-default"
                                          : "hover:scale-[1.05] active:scale-[0.95] cursor-pointer " + (dark
                                            ? "border-slate-800 bg-slate-900 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300"
                                            : "border-slate-200 bg-white text-emerald-605 hover:bg-slate-50 hover:text-emerald-700")
                                      )}
                                    >
                                      <CheckCircle2 size={13.5} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </>
                    ) : (
                      <>
                        <thead>
                          <tr
                            className={cls(
                              "border-b text-[10px] font-extrabold tracking-widest uppercase",
                              dark ? "bg-[#141927] border-slate-800 text-slate-400" : "bg-slate-55 border-slate-200 text-slate-500"
                            )}
                          >
                            <th className="py-3.5 px-4 select-none text-center w-10">
                              <input
                                type="checkbox"
                                checked={paginatedBreaches.length > 0 && paginatedBreaches.every((b) => selectedBreaches.includes(b.id))}
                                onChange={handleSelectAll}
                                className={cls("rounded focus:ring-blue-500/40 cursor-pointer w-3.5 h-3.5", dark ? "bg-slate-900 border-slate-700 text-blue-500" : "bg-white border-slate-300 text-blue-655")}
                              />
                            </th>
                            <th className="py-3.5 px-5 select-none text-center w-14 whitespace-nowrap">S.No</th>
                            <th className="py-3.5 px-5 select-none whitespace-nowrap">ID</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Reported By</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Created At</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Updated On</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Incident</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Description</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Root Cause</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Affected System</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Category</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Severity</th>
                            <th className="py-3.5 px-4 select-none text-right whitespace-nowrap">DPS Affected</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">Status</th>
                            <th className="py-3.5 px-4 select-none whitespace-nowrap">DPB Clock</th>
                            <th className="py-3.5 px-5 select-none text-center whitespace-nowrap">Actions</th>
                          </tr>
                        </thead>
                        <tbody className={cls("divide-y", dark ? "divide-slate-800 bg-[#0f1117]" : "divide-slate-100 bg-white")}>
                          {paginatedBreaches.length === 0 ? (
                            <tr>
                              <td colSpan={16} className="py-8 text-center text-xs font-bold text-slate-400 italic">
                                No breach incidents found matching the filter.
                              </td>
                            </tr>
                          ) : (
                            paginatedBreaches.map((breach, idx) => (
                              <tr
                                key={breach.id}
                                onClick={() => setSelectedBreachId(breach.id)}
                                className={cls(
                                  "transition-all duration-150 cursor-pointer border-l-2",
                                  selectedBreach && selectedBreach.id === breach.id
                                    ? (dark ? "bg-slate-800/40 border-l-red-500" : "bg-blue-50/40 border-l-red-500")
                                    : (dark ? "border-l-transparent hover:bg-slate-850/30" : "border-l-transparent hover:bg-slate-55/50")
                                )}
                              >
                                <td className="py-4 px-4 text-center w-10" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedBreaches.includes(breach.id)}
                                    onChange={() => handleSelectRow(breach.id)}
                                    className={cls("rounded focus:ring-blue-500/40 cursor-pointer w-3.5 h-3.5", dark ? "bg-slate-900 border-slate-700 text-blue-500" : "bg-white border-slate-300 text-blue-655")}
                                  />
                                </td>
                                <td className={cls("py-4 px-5 text-xs font-bold text-center", dark ? "text-slate-400" : "text-slate-505")}>
                                  {(currentPage - 1) * PAGE_SIZE + idx + 1}
                                </td>
                                <td className="py-4 px-5 whitespace-nowrap">
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleIdClick(breach);
                                      }}
                                      className="text-red-500 hover:text-red-650 hover:underline font-extrabold text-sm text-left transition-all duration-150"
                                    >
                                      {breach.formattedId || breach.id}
                                    </button>
                                    {breach.status !== "Closed" && breach.status !== "Remediated" && (
                                      <Flag size={12} className="text-red-500 fill-red-500 animate-pulse flex-shrink-0" title="Unresolved Incident" />
                                    )}
                                  </div>
                                </td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{breach.reportedBy || "—"}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{formatFriendlyDateTime(breach.createdAt)}</td>
                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>{formatFriendlyDateTime(breach.updatedAt)}</td>
                                                                <td

                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}

                                  onClick={(e) => {

                                    e.stopPropagation();

                                    setSelectedTextModal({ title: "Incident Details", content: breach.incident });

                                  }}

                                >

                                  {breach.incident && breach.incident.length > 25 ? breach.incident.substring(0, 25) + "..." : (breach.incident || "—")}

                                </td>
                                                                                                <td

                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}

                                  onClick={(e) => {

                                    e.stopPropagation();

                                    setSelectedTextModal({ title: "Description", content: breach.description });

                                  }}

                                >

                                  {breach.description && breach.description.length > 25 ? breach.description.substring(0, 25) + "..." : (breach.description || "—")}

                                </td>

                                <td

                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}

                                  onClick={(e) => {

                                    e.stopPropagation();

                                    setSelectedTextModal({ title: "Root Cause", content: breach.rootCause });

                                  }}

                                >

                                  {breach.rootCause && breach.rootCause.length > 25 ? breach.rootCause.substring(0, 25) + "..." : (breach.rootCause || "—")}

                                </td>

                                <td

                                  className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap cursor-pointer hover:text-blue-500 hover:underline", dark ? "text-slate-105" : "text-slate-905")}

                                  onClick={(e) => {

                                    e.stopPropagation();

                                    setSelectedTextModal({ title: "Affected System", content: breach.affectedSystem });

                                  }}

                                >

                                  {breach.affectedSystem && breach.affectedSystem.length > 25 ? breach.affectedSystem.substring(0, 25) + "..." : (breach.affectedSystem || "—")}

                                </td>

                                <td className={cls("py-4 px-4 text-sm font-bold whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>
                                  {breach.category}
                                </td>
                                <td className="py-4 px-4">
                                  <SeverityBadge severity={breach.severity} dark={dark} />
                                </td>
                                <td className={cls("py-4 px-4 text-sm font-bold text-right whitespace-nowrap", dark ? "text-slate-105" : "text-slate-905")}>
                                  {breach.dpsAffected}
                                </td>
                                <td className="py-4 px-4 whitespace-nowrap">
                                  <StatusBadge status={breach.status} dark={dark} />
                                </td>
                                <td className="py-4 px-4 whitespace-nowrap">
                                  <ClockBadge dpbClock={breach.detectionDateTime ? getDpbClock(breach.detectionDateTime, breach.dpoNotified, now, breach.status, breach.updatedAt) : (breach.dpbClock || { label: "—", overdue: false, notified: false, closed: false })} dark={dark} />
                                </td>
                                <td className="py-4 px-5" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-2">
                                    {/* Mail Alert Action Button */}
                                    <button
                                      type="button"
                                      title="Send Email Alert"
                                      onClick={() => handleSendEmail(breach)}
                                      disabled={sendingEmailId === breach.id}
                                      className={cls(
                                        "p-1.5 rounded-lg border transition-all shadow-sm hover:scale-[1.05] active:scale-[0.95]",
                                        sendingEmailId === breach.id ? "opacity-50 cursor-wait" : "cursor-pointer",
                                        dark
                                          ? "border-slate-800 bg-slate-900 text-blue-400 hover:bg-slate-800 hover:text-blue-300"
                                          : "border-slate-200 bg-white text-blue-600 hover:bg-slate-50 hover:text-blue-700"
                                      )}
                                    >
                                      {sendingEmailId === breach.id ? (
                                        <div className="w-3.5 h-3.5 border-2 border-t-transparent border-blue-500 rounded-full animate-spin" />
                                      ) : (
                                        <Mail size={13.5} />
                                      )}
                                    </button>

                                    {/* Edit Action Button */}
                                    <button
                                      type="button"
                                      title={["Closed", "Remediated"].includes(breach.status) ? "Resolved incidents cannot be edited" : "Edit Breach Record"}
                                      disabled={["Closed", "Remediated"].includes(breach.status)}
                                      onClick={() => handleEditBreachClick(breach)}
                                      className={cls(
                                        "p-1.5 rounded-lg border transition-all shadow-sm",
                                        ["Closed", "Remediated"].includes(breach.status)
                                          ? "opacity-45 cursor-not-allowed border-slate-200 text-slate-400"
                                          : "hover:scale-[1.05] active:scale-[0.95] cursor-pointer " + (dark
                                            ? "border-slate-800 bg-slate-900 text-amber-400 hover:bg-slate-800 hover:text-amber-300"
                                            : "border-slate-200 bg-white text-amber-605 hover:bg-slate-50 hover:text-amber-700")
                                      )}
                                    >
                                      <Pencil size={13.5} />
                                    </button>

                                    {/* Resolve Action Button */}
                                    <button
                                      type="button"
                                      title={["Closed", "Remediated"].includes(breach.status) ? "Resolved" : "Mark as Resolved"}
                                      disabled={["Closed", "Remediated"].includes(breach.status)}
                                      onClick={() => {
                                        setResolveBreachId(breach.id);
                                        setResolveComment("");
                                        setResolveFiles([]);
                                        setShowResolveModal(true);
                                      }}
                                      className={cls(
                                        "p-1.5 rounded-lg border transition-all shadow-sm",
                                        ["Closed", "Remediated"].includes(breach.status)
                                          ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-500 opacity-80 cursor-default"
                                          : "hover:scale-[1.05] active:scale-[0.95] cursor-pointer " + (dark
                                            ? "border-slate-800 bg-slate-900 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300"
                                            : "border-slate-200 bg-white text-emerald-600 hover:bg-slate-50 hover:text-emerald-700")
                                      )}
                                    >
                                      <CheckCircle2 size={13.5} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </>
                    )}
                  </table>
                </div>

                {!viewingAgentBreaches && filtered.length > 0 && (
                  <div className={cls(
                    "flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t transition-all",
                    dark ? "border-slate-800 bg-[#0f1117] text-slate-400" : "border-slate-100 bg-white text-slate-500"
                  )}>
                    <div className="text-xs font-semibold">
                      Showing <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{(currentPage - 1) * PAGE_SIZE + 1}</span> to <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{Math.min(currentPage * PAGE_SIZE, filtered.length)}</span> of <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{filtered.length}</span> breaches
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={cls(
                          "px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                          dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-750" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55"
                        )}
                      >
                        Previous
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className={cls(
                              "w-8 h-8 rounded-xl text-xs font-bold transition-all hover:scale-[1.05] active:scale-[0.95]",
                              currentPage === page
                                ? "bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md font-extrabold"
                                : dark
                                  ? "border border-slate-800 text-slate-400 hover:bg-slate-850"
                                  : "border border-slate-200 text-slate-700 hover:bg-slate-55"
                            )}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={cls(
                          "px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                          dark ? "bg-slate-800 border-slate-700 text-slate-305 hover:bg-slate-750" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55"
                        )}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {viewingAgentBreaches && agentTotalCount > 0 && (
                  <div className={cls(
                    "flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t transition-all",
                    dark ? "border-slate-808 bg-[#0f1117] text-slate-400" : "border-slate-100 bg-white text-slate-500"
                  )}>
                    <div className="text-xs font-semibold">
                      Showing <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{(agentCurrentPage - 1) * PAGE_SIZE + 1}</span> to <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{Math.min(agentCurrentPage * PAGE_SIZE, agentTotalCount)}</span> of <span className={cls("font-bold", dark ? "text-slate-200" : "text-slate-800")}>{agentTotalCount}</span> agent breaches
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fetchAgentBreaches(Math.max(agentCurrentPage - 1, 1))}
                        disabled={agentCurrentPage === 1}
                        className={cls(
                          "px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                          dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-750" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55"
                        )}
                      >
                        Previous
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: agentTotalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={() => fetchAgentBreaches(page)}
                            className={cls(
                              "w-8 h-8 rounded-xl text-xs font-bold transition-all hover:scale-[1.05] active:scale-[0.95]",
                              agentCurrentPage === page
                                ? "bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md font-extrabold"
                                : dark
                                  ? "border border-slate-800 text-slate-400 hover:bg-slate-850"
                                  : "border border-slate-200 text-slate-700 hover:bg-slate-55"
                            )}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => fetchAgentBreaches(Math.min(agentCurrentPage + 1, agentTotalPages))}
                        disabled={agentCurrentPage === agentTotalPages}
                        className={cls(
                          "px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                          dark ? "bg-slate-800 border-slate-700 text-slate-305 hover:bg-slate-750" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-55"
                        )}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">
              {/* Report New Breach Card Action Link */}
              <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60 hover:border-slate-300 hover:shadow-md")}>
                <div className="flex items-center justify-between mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                  <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-800")} style={{ fontFamily: "'Sora', sans-serif" }}>
                    REPORT NEW BREACH
                  </h2>
                  <span className={cls("px-2.5 py-1 rounded-xl text-[10px] font-bold border", dark ? "bg-slate-800 border-slate-700 text-slate-300" : "bg-slate-50 border-slate-200 text-slate-600")}>
                    Trigger Assessment
                  </span>
                </div>

                <div className="space-y-4 py-4 text-center">
                  <p className="text-sm text-slate-400 font-medium leading-relaxed">
                    Under the DPDP Act 2023, data breaches must be assessed and reported immediately. Use our step-by-step reporting workspace to catalog compliance details.
                  </p>
                  <button
                    onClick={handleNewBreachClick}
                    data-tour-id="tour-breach-report"
                    className="mt-4 px-6 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 mx-auto"
                  >
                    <Plus size={16} />
                    <span>Launch Breach Reporting Workspace</span>
                  </button>
                </div>
              </div>

              {/* Notification Checklist */}
              <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60 hover:border-slate-300 hover:shadow-md")} data-tour-id="tour-breach-checklist">
                <div className="flex items-center justify-between mb-2 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                  <div>
                    <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-800")} style={{ fontFamily: "'Sora', sans-serif" }}>
                      NOTIFICATION CHECKLIST
                    </h2>
                    {selectedBreach ? (
                      <p className="text-[10px] text-slate-400 font-bold mt-0.5">CASE: {selectedBreach.formattedId}</p>
                    ) : (
                      <p className="text-[10px] text-slate-400 font-bold mt-0.5">NO ACTIVE CASE</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-extrabold text-emerald-500 font-display" style={{ fontFamily: "'Sora', sans-serif" }}>{checklist_done}</span>
                    <span className={cls("text-sm font-semibold", dark ? "text-slate-300" : "text-slate-700")}>/{dynamicChecklist.length || 8} done</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-2 rounded-full mb-5 overflow-hidden relative" style={{ background: dark ? "#1e293b" : "#f1f5f9" }}>
                  <div className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${dynamicChecklist.length > 0 ? (checklist_done / dynamicChecklist.length) * 100 : 0}%`, background: "linear-gradient(90deg, #10b981 30%, #f59e0b 70%, #ef4444 100%)" }} />
                </div>

                <div className="space-y-2.5 overflow-y-auto scrollbar-thin pr-1" style={{ maxHeight: "250px" }}>
                  {dynamicChecklist.length === 0 ? (
                    <p className="text-xs text-slate-400 font-bold italic py-8 text-center">No active breach checklist to display.</p>
                  ) : (
                    dynamicChecklist.map((item, i) => (
                      <div
                        key={i}
                        className={cls(
                          "flex items-start gap-3 rounded-xl px-4 py-3 border transition-all duration-200 hover:translate-x-0.5",
                          item.done
                            ? (dark ? "bg-emerald-950/10 border-emerald-900/30 text-slate-300" : "bg-emerald-50/40 border-emerald-100 text-slate-750")
                            : (dark ? "bg-slate-900/40 border-slate-800 text-slate-400" : "bg-slate-55/50 border-slate-200 text-slate-500")
                        )}
                      >
                        <div
                          className={cls(
                            "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border transition-all duration-300",
                            item.done
                              ? "bg-emerald-500 border-emerald-500 text-white"
                              : (dark ? "bg-slate-800 border-slate-700 text-transparent" : "bg-white border-slate-300 text-transparent")
                          )}
                        >
                          {item.done && <Check size={10} strokeWidth={3} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className={cls("text-xs font-bold leading-none", item.done ? (dark ? "text-slate-205" : "text-slate-805") : "text-slate-505")}>
                              {item.label}
                            </p>
                            {item.done ? (
                              <span className="text-[9px] font-extrabold text-emerald-500 uppercase tracking-wider select-none">✓ Done</span>
                            ) : (
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider select-none">Pending</span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1 font-medium leading-relaxed">{item.sub}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          /* ════════════════════════════════════════════════════════════════
             LOG NEW BREACH WIZARD FORM VIEW
             ════════════════════════════════════════════════════════════════ */
          <motion.div
            key="wizard-form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-6 w-full"
          >
            {/* Header with Back Button */}
            <div className="flex items-center justify-between border-b pb-4" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogForm(false)}
                  className={cls(
                    "p-2 rounded-xl border transition-all flex items-center justify-center shadow-sm",
                    dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <ArrowLeft size={16} />
                </button>
                <div>
                  <h1 className="text-xl font-extrabold tracking-tight font-display" style={{ fontFamily: "'Sora', sans-serif" }}>
                    {editBreachId ? "Edit Breach Incident" : "Log New Breach Incident"}
                  </h1>
                  <p className="text-xs text-slate-400 font-semibold mt-0.5">Start 72-hour regulatory notification timeline</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLogForm(false)}
                className={cls(
                  "px-4 py-2 rounded-xl text-xs font-bold border transition-all shadow-sm",
                  dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                )}
              >
                Cancel Wizard
              </button>
            </div>

            {/* Wizard Steps Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-4 py-3 border rounded-2xl shadow-sm transition-all"
              style={{ background: dark ? "#111827" : "#ffffff", borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
              {/* Step 1 */}
              <div className="flex items-center gap-2.5">
                <div className={cls(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all",
                  wizardStep > 1 ? "bg-emerald-500 text-white" :
                    wizardStep === 1 ? "bg-red-500 text-white" :
                      (dark ? "bg-slate-900 border border-slate-800 text-slate-400" : "bg-slate-100 border border-slate-200 text-slate-500")
                )}>
                  {wizardStep > 1 ? <Check size={14} /> : "1"}
                </div>
                <div>
                  <p className={cls("text-xs font-bold font-display transition-colors",
                    wizardStep >= 1 ? (dark ? "text-slate-100" : "text-slate-900") : (dark ? "text-slate-550" : "text-slate-400")
                  )}>Breach Details</p>
                  <p className="text-[10px] text-slate-400 font-semibold">Provide incident information</p>
                </div>
              </div>
              <div className={cls("hidden md:block flex-1 h-px transition-colors mx-4",
                wizardStep > 1 ? "bg-emerald-500" : (dark ? "bg-slate-800" : "bg-slate-200")
              )} />

              {/* Step 2 */}
              <div className={cls("flex items-center gap-2.5 transition-all", wizardStep < 2 && "opacity-60")}>
                <div className={cls(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all border",
                  wizardStep > 2 ? "bg-emerald-500 border-emerald-500 text-white" :
                    wizardStep === 2 ? "bg-red-500 border-red-500 text-white" :
                      (dark ? "bg-slate-900 border-slate-800 text-slate-400" : "bg-slate-100 border-slate-200 text-slate-500")
                )}>
                  {wizardStep > 2 ? <Check size={14} /> : "2"}
                </div>
                <div>
                  <p className={cls("text-xs font-bold font-display transition-colors",
                    wizardStep >= 2 ? (dark ? "text-slate-100" : "text-slate-900") : (dark ? "text-slate-550" : "text-slate-400")
                  )}>Impact &amp; Notification</p>
                  <p className="text-[10px] text-slate-405 font-semibold">Assess impact and notify</p>
                </div>
              </div>
              <div className={cls("hidden md:block flex-1 h-px transition-colors mx-4",
                wizardStep > 2 ? "bg-emerald-500" : (dark ? "bg-slate-800" : "bg-slate-200")
              )} />

              {/* Step 3 */}
              <div className={cls("flex items-center gap-2.5 transition-all", wizardStep < 3 && "opacity-60")}>
                <div className={cls(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all border",
                  wizardStep === 3 ? "bg-red-500 border-red-500 text-white" :
                    (dark ? "bg-slate-900 border-slate-800 text-slate-400" : "bg-slate-100 border-slate-200 text-slate-500")
                )}>
                  3
                </div>
                <div>
                  <p className={cls("text-xs font-bold font-display transition-colors",
                    wizardStep >= 3 ? (dark ? "text-slate-100" : "text-slate-900") : (dark ? "text-slate-550" : "text-slate-400")
                  )}>Review &amp; Submit</p>
                  <p className="text-[10px] text-slate-405 font-semibold">Review and submit request</p>
                </div>
              </div>
            </div>

            {/* Wizard Form Panels */}
            <form onSubmit={handleFormSubmit} className="space-y-6">

              {/* STEP 1: Breach Details */}
              {wizardStep === 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 1. Basic Information */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <FileText size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-805")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Basic Information
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-405 mb-1.5 select-none">Breach ID (Auto Generated)</label>
                        <input type="text" readOnly value={`BR-2024-0${31 + breachesList.length - INITIAL_BREACHES.length + 1}`}
                          className={cls(inputCls, "opacity-75 cursor-not-allowed", dark ? "bg-slate-800 border-slate-700" : "bg-slate-55 border-slate-205")} />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Incident Title *</label>
                        <input type="text" placeholder="Enter incident title" value={incidentTitle} onChange={(e) => setIncidentTitle(e.target.value)} className={cls(inputCls, formErrors.incidentTitle && "border-red-500 focus:ring-red-500/20")} />
                        {formErrors.incidentTitle && (
                          <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.incidentTitle}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Category *</label>
                          <div className="relative">
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className={cls(inputCls, "appearance-none cursor-pointer")}
                              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 12px center', backgroundSize: '14px', backgroundRepeat: 'no-repeat' }}>
                              {["Confidentiality", "Availability", "Integrity", "Availability + Integrity"].map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Severity *</label>
                          <div className="relative">
                            <select value={severity} onChange={(e) => setSeverity(e.target.value)} className={cls(inputCls, "appearance-none cursor-pointer")}
                              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 12px center', backgroundSize: '14px', backgroundRepeat: 'no-repeat' }}>
                              {["Critical", "High", "Medium", "Low"].map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Detection Date &amp; Time *</label>
                          <input type="datetime-local" readOnly value={detectionDateTime}
                            className={cls(inputCls, "opacity-75 cursor-not-allowed", dark ? "bg-slate-800 border-slate-700" : "bg-slate-55 border-slate-205")} style={{ colorScheme: dark ? "dark" : "light" }} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Reported By *</label>
                          <div className="relative">
                            <select value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} className={cls(inputCls, "appearance-none cursor-pointer", formErrors.reportedBy && "border-red-500 focus:ring-red-500/20")}
                              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 12px center', backgroundSize: '14px', backgroundRepeat: 'no-repeat' }}>
                              <option value="">Select user</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.full_name}>{u.full_name}</option>
                              ))}
                            </select>
                            {formErrors.reportedBy && (
                              <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.reportedBy}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Department *</label>
                        <div className="relative">
                          <select value={department} onChange={(e) => setDepartment(e.target.value)} className={cls(inputCls, "appearance-none cursor-pointer", formErrors.department && "border-red-500 focus:ring-red-500/20")}
                            style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 12px center', backgroundSize: '14px', backgroundRepeat: 'no-repeat' }}>
                            <option value="">Select department</option>
                            {departments.map((d) => (
                              <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                          </select>
                          {formErrors.department && (
                            <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.department}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. Incident Details */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <Activity size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-855")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Incident Details
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Description *</label>
                        <textarea placeholder="Enter detailed description of the breach incident..." value={description} onChange={(e) => setDescription(e.target.value)}
                          rows={3.5} className={cls(inputCls, "resize-none", formErrors.description && "border-red-500 focus:ring-red-500/20")} />
                        {formErrors.description && (
                          <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.description}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Root Cause</label>
                          <input type="text" placeholder="Enter root cause" value={rootCause} onChange={(e) => setRootCause(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Affected System / Application</label>
                          <input type="text" placeholder="Enter system or application name" value={affectedSystem} onChange={(e) => setAffectedSystem(e.target.value)} className={inputCls} />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Data Type Affected *</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[
                            { label: "Customer Data", icon: User, color: "blue" },
                            { label: "Financial Data", icon: Scale, color: "emerald" },
                            { label: "Employee Data", icon: Briefcase, color: "blue" },
                            { label: "Health Data", icon: AlertTriangle, color: "red" },
                            { label: "Personal Information", icon: Shield, color: "purple" }
                          ].map((item) => {
                            const Icon = item.icon;
                            const selected = affectedDataTypes.includes(item.label);

                            // Style states
                            const btnCls = selected
                              ? item.color === "blue" ? "bg-blue-50 border-blue-200 text-blue-700"
                                : item.color === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                  : item.color === "red" ? "bg-red-50 border-red-200 text-red-700"
                                    : "bg-purple-50 border-purple-200 text-purple-700"
                              : dark ? "bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800"
                                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50";

                            return (
                              <button
                                key={item.label}
                                type="button"
                                onClick={() => handleDataTypeToggle(item.label)}
                                className={cls(
                                  "px-3.5 py-2.5 rounded-xl border text-xs font-bold flex items-center gap-2 shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]",
                                  btnCls
                                )}
                              >
                                <Icon size={13} />
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Impact & Notification */}
              {wizardStep === 2 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 3. Impact Assessment */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <TrendingUp size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-855")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Impact Assessment
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Number of Data Principals Affected *</label>
                          <input type="number" placeholder="Enter number" value={dpsAffected} onChange={(e) => setDpsAffected(e.target.value)} className={cls(inputCls, formErrors.dpsAffected && "border-red-500 focus:ring-red-500/20")} />
                          {formErrors.dpsAffected && (
                            <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.dpsAffected}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Estimated Records Impacted *</label>
                          <input type="number" placeholder="Enter number" value={recordsImpacted} onChange={(e) => setRecordsImpacted(e.target.value)} className={cls(inputCls, formErrors.recordsImpacted && "border-red-500 focus:ring-red-500/20")} />
                          {formErrors.recordsImpacted && (
                            <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.recordsImpacted}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Financial Impact (₹)</label>
                          <input type="text" placeholder="Enter amount" value={financialImpact} onChange={(e) => setFinancialImpact(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Risk Level *</label>
                          <div className="flex items-center gap-4 mt-2 py-1 select-none">
                            {["Low", "Medium", "High", "Critical"].map((rl) => (
                              <label key={rl} className="flex items-center gap-1.5 cursor-pointer text-xs font-bold text-slate-600">
                                <input
                                  type="radio"
                                  name="riskLevel"
                                  value={rl}
                                  checked={riskLevel === rl}
                                  onChange={(e) => setRiskLevel(e.target.value)}
                                  className={cls(
                                    "w-3.5 h-3.5 border focus:ring-0 cursor-pointer",
                                    rl === "Critical" ? "text-red-500" : rl === "High" ? "text-orange-500" : "text-blue-500"
                                  )}
                                />
                                <span>{rl}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Potential Impact Description</label>
                        <textarea placeholder="Describe the potential impact of this breach..." value={potentialImpactDesc} onChange={(e) => setPotentialImpactDesc(e.target.value)}
                          rows={2.5} className={cls(inputCls, "resize-none")} />
                      </div>
                    </div>
                  </div>

                  {/* 4. Notification & Compliance */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <Shield size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-855")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Notification &amp; Compliance
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">DPO Notified? *</label>
                          <div className="flex gap-2">
                            {["Yes", "No"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setDpoNotified(opt)}
                                className={cls(
                                  "flex-1 py-2 rounded-xl text-xs font-bold border transition-all shadow-sm",
                                  dpoNotified === opt
                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                    : dark ? "bg-slate-900 border-slate-700 text-slate-400" : "bg-white border-slate-200 text-slate-500"
                                )}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">DPB Notification Required? *</label>
                          <div className="flex gap-2">
                            {["Yes", "No"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setDpbNotificationRequired(opt)}
                                className={cls(
                                  "flex-1 py-2 rounded-xl text-xs font-bold border transition-all shadow-sm",
                                  dpbNotificationRequired === opt
                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                    : dark ? "bg-slate-900 border-slate-700 text-slate-400" : "bg-white border-slate-200 text-slate-500"
                                )}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">DPO Notification Date &amp; Time *</label>
                          <input type="datetime-local" value={dpoNotificationDate} onChange={(e) => setDpoNotificationDate(e.target.value)}
                            className={cls(inputCls, formErrors.dpoNotificationDate && "border-red-500 focus:ring-red-500/20")} style={{ colorScheme: dark ? "dark" : "light" }} />
                          {formErrors.dpoNotificationDate && (
                            <p className="text-red-500 text-[10px] font-bold mt-1">{formErrors.dpoNotificationDate}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-405 mb-1.5 select-none">DPB Notification Due By</label>
                          <div className={cls("flex items-center gap-2 px-3 py-2.5 rounded-xl border font-bold text-xs shadow-sm select-none",
                            dark ? "bg-orange-950/20 border-orange-900/60 text-orange-400" : "bg-[#fff3c7]/30 border-orange-200 text-orange-700")}>
                            <Clock size={13} className="text-orange-500 animate-pulse" />
                            <span>{getDpbDueBy()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Data Principal Notification Required? *</label>
                          <div className="flex gap-2">
                            {["Yes", "No"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setDataPrincipalNotificationRequired(opt)}
                                className={cls(
                                  "flex-1 py-2 rounded-xl text-xs font-bold border transition-all shadow-sm",
                                  dataPrincipalNotificationRequired === opt
                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                    : dark ? "bg-slate-900 border-slate-700 text-slate-400" : "bg-white border-slate-200 text-slate-500"
                                )}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1.5">Regulatory Authority</label>
                          <div className="relative">
                            <select value={regulatoryAuthority} onChange={(e) => setRegulatoryAuthority(e.target.value)} className={cls(inputCls, "appearance-none cursor-pointer")}
                              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, backgroundPosition: 'right 12px center', backgroundSize: '14px', backgroundRepeat: 'no-repeat' }}>
                              <option value="">Select authority</option>
                              <option value="Data Protection Board of India">Data Protection Board of India</option>
                              <option value="State IT Authority">State IT Authority</option>
                              <option value="CERT-In">CERT-In (Cyber Emergency Response Team)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Notification Warning Message Box */}
                      <div className={cls("rounded-xl p-3.5 border flex items-start gap-2.5 text-[11px] leading-relaxed font-semibold transition-all",
                        dark ? "bg-blue-950/15 border-blue-900/40 text-blue-300" : "bg-[#eff6ff] border-blue-100 text-blue-700")}>
                        <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                        <p>DPDP Act 2023 Section 9(1): Notification to DPB within 72h of becoming aware.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Review & Submit */}
              {wizardStep === 3 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 5. Attachments & Evidence */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <Paperclip size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-855")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Attachments &amp; Evidence
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      {/* Dropzone container */}
                      <div
                        onClick={handleMockFileUpload}
                        className={cls(
                          "rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 select-none shadow-inner",
                          dark ? "bg-slate-955 border-slate-800 hover:bg-slate-900/45 hover:border-slate-750" : "bg-slate-50/50 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                        )}
                      >
                        <Upload size={24} className="text-slate-400 mb-2.5 animate-bounce" style={{ animationDuration: '2s' }} />
                        <p className={cls("text-xs font-bold", dark ? "text-slate-200" : "text-slate-700")}>Drag and drop files here or click to browse</p>
                        <p className="text-[10px] text-slate-400 mt-1 font-semibold">Supported formats: PDF, DOCX, XLSX, PNG, JPG (Max 10MB each)</p>
                      </div>

                      {/* Uploaded Files container */}
                      <div className={cls("rounded-xl p-4 border", dark ? "bg-slate-950/20 border-slate-800" : "bg-slate-50/20 border-slate-200")}>
                        <p className={cls("text-xs font-black tracking-wider uppercase mb-3 text-slate-405")}>Uploaded Files ({uploadedFiles.length})</p>
                        {uploadedFiles.length === 0 ? (
                          <p className="text-xs text-slate-400 font-semibold italic select-none py-6 text-center">No files uploaded yet.</p>
                        ) : (
                          <div className="space-y-2 max-h-[120px] overflow-y-auto pr-1 scrollbar-thin">
                            {uploadedFiles.map((file) => (
                              <div key={file} className={cls("flex items-center justify-between p-2 rounded-lg border text-xs font-bold",
                                dark ? "bg-slate-900 border-slate-800 text-slate-300" : "bg-white border-slate-200 text-slate-700")}>
                                <div className="flex items-center gap-2 truncate">
                                  <FileText size={13} className="text-blue-500 flex-shrink-0" />
                                  <span className="truncate">{file}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleFileDelete(file); }}
                                  className="text-red-500 hover:text-red-650 transition-colors p-1"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 6. Incident Summary Review */}
                  <div className={cls("rounded-2xl p-5 border transition-all duration-300 shadow-sm hover:border-slate-300 hover:shadow-md", dark ? "bg-slate-900/90 border-slate-800 hover:border-slate-700 hover:shadow-lg" : "bg-white border-slate-200/60")}>
                    <div className="flex items-center gap-2 mb-5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                      <Eye size={16} className="text-blue-500" />
                      <h2 className={cls("text-sm font-black tracking-wider uppercase font-display", dark ? "text-slate-200" : "text-slate-855")} style={{ fontFamily: "'Sora', sans-serif" }}>
                        Incident Summary Review
                      </h2>
                    </div>

                    <div className="space-y-4 text-xs">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Breach ID</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>BR-2024-0{31 + breachesList.length - INITIAL_BREACHES.length + 1}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Incident Title</p>
                          <p className={cls("font-extrabold mt-0.5 truncate", dark ? "text-slate-100" : "text-slate-800")}>{incidentTitle}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Category &amp; Severity</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>{category} / {severity}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Department</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>{department}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Detected &amp; Reported By</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>{reportedBy} ({detectionDateTime ? detectionDateTime.replace('T', ' ') : ''})</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Risk Level</p>
                          <p className="font-extrabold mt-0.5 text-red-500">{riskLevel}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">DPs Affected</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>{dpsAffected ? Number(dpsAffected).toLocaleString() : "0"}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Records Impacted</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>{recordsImpacted ? Number(recordsImpacted).toLocaleString() : "0"}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">DPO Notified Date &amp; Time</p>
                          <p className={cls("font-extrabold mt-0.5", dark ? "text-slate-100" : "text-slate-800")}>
                            {dpoNotified === "Yes" ? (dpoNotificationDate ? dpoNotificationDate.replace('T', ' ') : '—') : "No"}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">DPB Notification Due By</p>
                          <p className="font-extrabold mt-0.5 text-orange-500">{getDpbDueBy().split(' ')[0]}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Description</p>
                        <p className={cls("mt-1 leading-relaxed font-semibold max-h-[70px] overflow-y-auto pr-1 scrollbar-thin", dark ? "text-slate-300" : "text-slate-700")}>{description}</p>
                      </div>

                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Affected Data Types</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {affectedDataTypes.map(t => (
                            <span key={t} className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-slate-800 dark:text-blue-300 dark:border-slate-700">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Action Footer Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                {wizardStep === 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowLogForm(false);
                      }}
                      className={cls(
                        "px-5 py-2.5 rounded-xl text-sm font-bold border transition-all shadow-sm hover:scale-[1.01] active:scale-[0.99]",
                        dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                      )}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          if (validateStep1()) {
                            setWizardStep(2);
                          }
                        } catch (err) {
                          console.error("Error transitioning to Step 2:", err);
                        }
                      }}
                      className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] flex items-center gap-2"
                    >
                      <span>Continue</span>
                    </button>
                  </>
                ) : wizardStep === 2 ? (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setWizardStep(1);
                      }}
                      className={cls(
                        "px-5 py-2.5 rounded-xl text-sm font-bold border transition-all shadow-sm hover:scale-[1.01] active:scale-[0.99]",
                        dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                      )}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          if (validateStep2()) {
                            setWizardStep(3);
                          }
                        } catch (err) {
                          console.error("Error transitioning to Step 3:", err);
                        }
                      }}
                      className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] flex items-center gap-2"
                    >
                      <span>Continue</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setWizardStep(2);
                      }}
                      className={cls(
                        "px-5 py-2.5 rounded-xl text-sm font-bold border transition-all shadow-sm hover:scale-[1.01] active:scale-[0.99]",
                        dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                      )}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] flex items-center gap-2 disabled:opacity-50"
                    >
                      {submitting ? (
                        <div className="w-4.5 h-4.5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                      ) : (
                        <CheckCircle2 size={15} />
                      )}
                      <span>{submitting ? "Submitting..." : editBreachId ? "Update Breach Report" : "Submit Breach Report"}</span>
                    </button>
                  </>
                )}
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>

    {/* Details & Edit Sidebar Drawer */}
      <AnimatePresence>
        {showDetailSidebar && sidebarBreach && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetailSidebar(false)}
              className="fixed inset-x-0 bottom-0 top-[52px] z-[100] bg-slate-900/60 backdrop-blur-sm"
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={cls(
                "fixed top-[52px] bottom-0 right-0 z-[101] w-[50vw] shadow-2xl flex flex-col border-l h-[calc(100vh-52px)] overflow-hidden",
                dark ? "bg-[#0b0f19] border-slate-800 text-slate-100" : "bg-white border-slate-205 text-slate-900"
              )}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                <div>
                  <h3 className="text-lg font-extrabold tracking-tight font-display text-red-500" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Breach Incident Explorer
                  </h3>
                  <p className="text-xs text-slate-450 mt-0.5 font-bold">
                    Case ID: {sidebarBreach.formattedId || sidebarBreach.id} · Status: {sidebarBreach.status}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1 font-semibold">
                    Created At: {formatFriendlyDateTime(sidebarBreach.createdAt || sidebarBreach.created_at)}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5 font-semibold">
                    Updated On: {formatFriendlyDateTime(sidebarBreach.updatedAt || sidebarBreach.updated_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDetailSidebar(false)}
                  className={cls(
                    "p-2 rounded-xl border transition-all hover:bg-slate-100 dark:hover:bg-slate-800",
                    dark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-600"
                  )}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Tab Selector Buttons */}
              <div className="flex gap-2 px-6 py-3 border-b select-none" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                <button
                  type="button"
                  onClick={() => setSidebarActiveTab("logs")}
                  className={cls(
                    "px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2",
                    sidebarActiveTab === "logs"
                      ? "bg-blue-500 text-white shadow-md"
                      : dark
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  )}
                >
                  <Mail size={14} />
                  <span>Email Logs ({emailLogsOnly.length})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarActiveTab("activity")}
                  className={cls(
                    "px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2",
                    sidebarActiveTab === "activity"
                      ? "bg-blue-500 text-white shadow-md"
                      : dark
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  )}
                >
                  <Activity size={14} />
                  <span>Activity Log ({editLogs.length})</span>
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
                {sidebarActiveTab === "logs" ? (
                  <div className="flex flex-col h-full justify-between">
                    <div className="space-y-4">
                      <div className="border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                        <h4 className="text-sm font-black tracking-wider uppercase flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                          <Mail size={16} className="text-blue-500" />
                          Email History &amp; Alerts
                        </h4>
                        <p className="text-xs text-slate-400 mt-1 font-semibold">
                          History of compliance email alerts sent to the reporter
                        </p>
                      </div>

                      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 scrollbar-thin">
                        {emailLogsOnly.length === 0 ? (
                          <p className="text-xs text-slate-450 font-bold italic py-8 text-center">No email notifications sent yet.</p>
                        ) : (
                          emailLogsOnly.map((log, idx) => (
                            <div
                              key={idx}
                              className={cls(
                                "p-3.5 rounded-xl border flex flex-col gap-1.5 transition-all",
                                dark ? "bg-slate-955 border-slate-800" : "bg-slate-50/50 border-slate-200"
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className={cls(
                                  "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border",
                                  log.type === "confirmation" || log.type === "resolution"
                                    ? "bg-blue-55 border-blue-105 text-blue-700 dark:bg-blue-955/20 dark:text-blue-300 dark:border-blue-900"
                                    : "bg-orange-55 border-orange-105 text-orange-700 dark:bg-orange-955/20 dark:text-orange-300 dark:border-orange-900"
                                )}>
                                  {log.type || "alert"}
                                </span>
                                <span className="text-[10px] text-slate-405 font-semibold">
                                  {formatFriendlyDateTime(log.sent_at)}
                                </span>
                              </div>
                              <p className={cls("text-xs font-bold leading-tight mt-0.5", dark ? "text-slate-200" : "text-slate-800")}>
                                {log.subject}
                              </p>
                              {log.details && (
                                <div className="mt-1.5 space-y-1 pl-2 border-l border-slate-300 dark:border-slate-700">
                                  {Array.isArray(log.details) ? (
                                    log.details.map((change, cIdx) => (
                                      <p key={cIdx} className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                                        • {change}
                                      </p>
                                    ))
                                  ) : (
                                    <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                                      {log.details}
                                    </p>
                                  )}
                                </div>
                              )}
                              <p className="text-[10px] text-slate-405 font-semibold truncate mt-1">
                                Recipient: {log.recipient}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                      <button
                        type="button"
                        onClick={() => handleSidebarSendEmail()}
                        disabled={sendingEmailId === sidebarBreach.id}
                        className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 transition-all shadow-md flex items-center justify-center gap-2"
                      >
                        {sendingEmailId === sidebarBreach.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                            <span>Sending Notification...</span>
                          </>
                        ) : (
                          <>
                            <Mail size={16} />
                            <span>Send Manual Compliance Alert</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col h-full justify-between">
                    <div className="space-y-4">
                      <div className="border-b pb-3" style={{ borderColor: dark ? "#1f2937" : "#f1f5f9" }}>
                        <h4 className="text-sm font-black tracking-wider uppercase flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                          <Activity size={16} className="text-emerald-500" />
                          Activity &amp; Edit History
                        </h4>
                        <p className="text-xs text-slate-400 mt-1 font-semibold">
                          Audit history of modifications made to this breach record
                        </p>
                      </div>

                      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 scrollbar-thin">
                        {editLogsLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="w-5 h-5 border-2 border-t-transparent border-emerald-500 rounded-full animate-spin" />
                            <span className="ml-2 text-xs text-slate-400 font-semibold">Loading edit history...</span>
                          </div>
                        ) : editLogs.length === 0 ? (
                          <p className="text-xs text-slate-450 font-bold italic py-8 text-center">No edits recorded yet.</p>
                        ) : (
                          editLogs.map((log, idx) => (
                            <div
                              key={log.id || idx}
                              className={cls(
                                "p-3.5 rounded-xl border flex flex-col gap-1.5 transition-all",
                                dark ? "bg-slate-955 border-slate-800" : "bg-slate-50/50 border-slate-200"
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border bg-emerald-55 border-emerald-105 text-emerald-700 dark:bg-emerald-955/20 dark:text-emerald-300 dark:border-emerald-900">
                                  Edit
                                </span>
                                <span className="text-[10px] text-slate-405 font-semibold">
                                  {formatFriendlyDateTime(log.created_at)}
                                </span>
                              </div>
                              <p className={cls("text-xs font-bold leading-tight mt-0.5", dark ? "text-slate-200" : "text-slate-800")}>
                                Updated by {log.edited_by}
                              </p>
                              {log.changed_fields && log.changed_fields.length > 0 && (
                                <div className="mt-1.5 space-y-1 pl-2 border-l border-emerald-500/40">
                                  {log.changed_fields.map((change, cIdx) => (
                                    <p key={cIdx} className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                                      • {change}
                                    </p>
                                  ))}
                                </div>
                              )}
                              <p className="text-[10px] text-slate-405 font-semibold truncate mt-1">
                                Editor: {log.editor_email}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Email Logs Modal */}
      <AnimatePresence>
        {showLogsModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={cls(
                "w-full max-w-lg rounded-2xl border p-6 shadow-2xl relative",
                dark ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900"
              )}
            >
              <div className="flex items-center justify-between border-b pb-4 mb-4" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                <div>
                  <h3 className="text-base font-extrabold tracking-tight font-display" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Email History - {selectedBreachIdForLogs}
                  </h3>
                  <p className="text-xs text-slate-450 font-semibold mt-0.5">List of email alerts sent to the reporter</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLogsModal(false)}
                  className={cls(
                    "px-3 py-1.5 rounded-xl text-xs font-bold border transition-all shadow-sm",
                    dark ? "bg-slate-800 border-slate-700 hover:text-white" : "bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100"
                  )}
                >
                  Close
                </button>
              </div>

              <div className="space-y-3.5 max-h-[350px] overflow-y-auto pr-1 scrollbar-thin">
                {selectedBreachLogs.length === 0 ? (
                  <p className="text-xs text-slate-450 font-bold italic py-8 text-center">No email notifications sent yet.</p>
                ) : (
                  selectedBreachLogs.map((log, idx) => (
                    <div
                      key={idx}
                      className={cls(
                        "p-3.5 rounded-xl border flex flex-col gap-1.5 transition-all",
                        dark ? "bg-slate-950/20 border-slate-800" : "bg-slate-50/50 border-slate-200"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={cls(
                          "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border",
                          log.type === "confirmation"
                            ? "bg-blue-50 border-blue-105 text-blue-700"
                            : log.type === "update"
                              ? "bg-emerald-50 border-emerald-105 text-emerald-700"
                              : "bg-orange-50 border-orange-105 text-orange-700"
                        )}>
                          {log.type}
                        </span>
                        <span className="text-[10px] text-slate-400 font-semibold">
                          {formatFriendlyDateTime(log.sent_at)}
                        </span>
                      </div>
                      <p className={cls("text-xs font-bold leading-tight mt-0.5", dark ? "text-slate-200" : "text-slate-800")}>
                        {log.subject}
                      </p>
                      {log.details && (
                        <div className="mt-1.5 space-y-1 pl-2 border-l border-slate-300 dark:border-slate-705">
                          {Array.isArray(log.details) ? (
                            log.details.map((change, cIdx) => (
                              <p key={cIdx} className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                                • {change}
                              </p>
                            ))
                          ) : (
                            <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                              {log.details}
                            </p>
                          )}
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 font-semibold truncate mt-1">
                        {log.type === "update" ? "Editor" : "Recipient"}: {log.recipient}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resolve Breach Modal */}
      <AnimatePresence>
        {showResolveModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={cls(
                "w-full max-w-lg rounded-2xl border p-6 shadow-2xl relative",
                dark ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900"
              )}
            >
              <div className="flex items-center justify-between border-b pb-4 mb-4" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                <div>
                  <h3 className="text-base font-extrabold tracking-tight font-display" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Resolve Breach Incident
                  </h3>
                  <p className="text-xs text-slate-450 font-semibold mt-0.5">
                    Case: {breachesList.find(b => b.id === resolveBreachId)?.formattedId || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowResolveModal(false);
                    setResolveBreachId(null);
                    setResolveComment("");
                    setResolveFiles([]);
                  }}
                  className={cls(
                    "px-3 py-1.5 rounded-xl text-xs font-bold border transition-all shadow-sm",
                    dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:text-white" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  Cancel
                </button>
              </div>

              <form onSubmit={handleResolveSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-450 mb-1.5 uppercase tracking-wider">
                    Resolution Comment / Action Taken *
                  </label>
                  <textarea
                    required
                    placeholder="Describe mitigation actions, containment measures, and overall resolution of this breach..."
                    value={resolveComment}
                    onChange={(e) => setResolveComment(e.target.value)}
                    rows={4}
                    className={cls(
                      "w-full px-3.5 py-2.5 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all shadow-sm font-medium resize-none",
                      dark ? "bg-slate-950 border-slate-800 text-slate-100 focus:border-slate-700" : "bg-white border-slate-200 text-slate-850 focus:border-slate-300"
                    )}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-450 mb-1.5 uppercase tracking-wider">
                    Mitigation Evidence / Attachments
                  </label>

                  {/* Dropzone container */}
                  <div
                    onClick={handleResolveFileUpload}
                    className={cls(
                      "rounded-xl border-2 border-dashed p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 select-none shadow-inner mb-3",
                      dark ? "bg-slate-955 border-slate-800 hover:bg-slate-900/45 hover:border-slate-750" : "bg-slate-50/50 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                    )}
                  >
                    <Upload size={20} className="text-slate-400 mb-1.5 animate-bounce" style={{ animationDuration: '2s' }} />
                    <p className={cls("text-xs font-bold", dark ? "text-slate-200" : "text-slate-700")}>Click to upload validation reports, approvals, logs</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 font-semibold">PDF, DOCX, XLSX up to 10MB (Simulated)</p>
                  </div>

                  {/* Uploaded Files list */}
                  <div className={cls("rounded-xl p-3 border", dark ? "bg-slate-955 border-slate-800" : "bg-slate-50/20 border-slate-200")}>
                    <p className="text-[10px] font-black tracking-wider uppercase text-slate-400 mb-2">Uploaded Evidence ({resolveFiles.length})</p>
                    {resolveFiles.length === 0 ? (
                      <p className="text-[11px] text-slate-400 font-semibold italic py-2 text-center select-none">No files attached yet.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-[100px] overflow-y-auto pr-1 scrollbar-thin">
                        {resolveFiles.map((file) => (
                          <div key={file} className={cls("flex items-center justify-between p-2 rounded-lg border text-xs font-semibold",
                            dark ? "bg-slate-900 border-slate-800 text-slate-300" : "bg-white border-slate-200 text-slate-750")}>
                            <div className="flex items-center gap-2 truncate">
                              <FileText size={12} className="text-blue-500 flex-shrink-0" />
                              <span className="truncate">{file}</span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleResolveFileDelete(file); }}
                              className="text-red-500 hover:text-red-700 transition-colors p-0.5"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResolveModal(false);
                      setResolveBreachId(null);
                      setResolveComment("");
                      setResolveFiles([]);
                    }}
                    className={cls(
                      "px-4 py-2 rounded-xl text-xs font-bold border transition-all shadow-sm hover:scale-[1.02] active:scale-[0.98]",
                      dark ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white" : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                    )}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resolving}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {resolving ? (
                      <div className="w-3.5 h-3.5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    ) : (
                      <CheckCircle2 size={13} />
                    )}
                    <span>{resolving ? "Resolving..." : "Confirm Resolution"}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Selected Text Details Modal */}
      <AnimatePresence>
        {selectedTextModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={cls(
                "w-full max-w-lg rounded-2xl border p-6 shadow-2xl relative",
                dark ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900"
              )}
            >
              <div className="flex items-center justify-between border-b pb-4 mb-4" style={{ borderColor: dark ? "#1f2937" : "#e2e8f0" }}>
                <div>
                  <h3 className="text-base font-extrabold tracking-tight font-display" style={{ fontFamily: "'Sora', sans-serif" }}>
                    {selectedTextModal.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedTextModal(null)}
                  className={cls(
                    "px-3 py-1.5 rounded-xl text-xs font-bold border transition-all shadow-sm",
                    dark ? "bg-slate-800 border-slate-700 hover:text-white" : "bg-slate-55 border-slate-200 text-slate-650 hover:bg-slate-100"
                  )}
                >
                  Close
                </button>
              </div>

              <div className={cls(
                "p-4 rounded-xl border text-sm font-medium leading-relaxed max-h-[300px] overflow-y-auto scrollbar-thin break-words",
                dark ? "bg-slate-955 border-slate-800 text-slate-300" : "bg-slate-50/50 border-slate-200 text-slate-750"
              )}>
                {selectedTextModal.content || "—"}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
